/* eslint react-hooks/exhaustive-deps: 0 */


import moment from "moment";
import { Col, message, Row } from "antd";
import { useEffect, useState, useRef, useLayoutEffect, useMemo, useCallback, memo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { officeDevice, syncClock, offlineDevice, syncOn, onlineLog, offlineLog } from "../../../assets/images";
import { removeCurrentStep, removeProcessState, selectCurrentAppState, setCurrentStep, setIsPaused, setPausedData, setProcessState, setStopButtonForRun, setProcessRunningModal, setCurrentNode } from "../../../redux/features/app/appSlice";
import { authStateSelector } from "../../../redux/features/auth/authSlice";
import { usePauseProcessMutation, useResumeProcessMutation, useStopActiveProcessMutation, useExecuteProcessMutation, useStartProcessMutation, useGetAllActiveProcessQuery } from "../../../redux/services/processSlice";
import { UIbutton, UIsecondaryButton } from "../../common/button";
import { ModalButton, ModalPrimaryText, ModalType, WebsocketKey } from "../../../config/enum";
import { UIConfirmModal } from "../../common/confirmModal";
import { useGetDeviceStatusQuery, useGetAllDeviceOfUserQuery } from "../../../redux/services/deviceSite";
import { compareIcon, dataSyncIcon, gpio, gpioSound, notifyIcon, printLabel, scanTag, waitIcon } from "../../../assets/icons";
import { UIImage } from "../../common/image";
import { ProcessName } from "../../../config/constants";
import { useLazyGetReferenceStockQuery } from "../../../redux/services/referenceStockSlice";
import { socket } from "../../../redux/services/webSocket";
import { Page } from "../../../routes/config";
import { ProcessPausedModal } from "../../common/pausedModal";
import { ProcessRunningModal } from "../../common/modal";

const shouldKeepPreviousTotalCycles = (prev: any, incoming: number | undefined) =>
  incoming !== undefined &&
  typeof prev?.totalCycles === "number" &&
  prev.totalCycles > 0 &&
  incoming === 0;

const OFFLINE_STATUSES = new Set([
  "offline",
  "disconnected",
  "unreachable",
  "connection-lost",
  "connection_lost",
  "inactive"
]);

const normalizeStatus = (status: any) => String(status || "").toLowerCase().trim();
const isOfflineStatus = (status: any) => OFFLINE_STATUSES.has(normalizeStatus(status));

const normalizeMacKey = (mac: any) => String(mac || "").replace(/[:-]/g, "").toUpperCase();

/** Parse stateId from execute/start/pause API payloads (root, data, or result). */
const readStateIdFromApiPayload = (payload: any): string | undefined => {
  if (payload == null) return undefined;
  const root = payload;
  const dataNode = root.data ?? {};
  const result = root.result ?? dataNode.result ?? {};
  const raw = root.stateId ?? dataNode.stateId ?? result.stateId;
  if (raw === undefined || raw === null) return undefined;
  const id = String(raw).trim();
  return id.length > 0 ? id : undefined;
};

/** State row from execute or active-process list: may expose stateId or Mongo _id / id. */
export const readStateIdFromNavRow = (row: any): string | undefined => {
  if (row == null || typeof row !== "object") return undefined;
  const explicit = row.stateId ?? row.state_id;
  if (explicit != null && explicit !== "") {
    const s = String(explicit).trim();
    if (s.length > 0) return s;
  }
  const oid = row._id;
  if (oid != null && typeof oid === "object" && oid !== null && "$oid" in oid) {
    const s = String((oid as { $oid?: string }).$oid ?? "").trim();
    if (s.length > 0) return s;
  }
  if (oid != null) {
    const s = String(oid).trim();
    if (s.length > 0) return s;
  }
  const id = row.id;
  if (id != null) {
    const s = String(id).trim();
    if (s.length > 0) return s;
  }
  return undefined;
};

export const DeviceDetail = (props: any) => {
  const { data,setSyncedData,syncedData, loadingBar, setLoadingBar, onProcessStateChange } = props;
  let beforeStep = data?.data?.steps
    ? data?.data?.steps
    : data?.data?.processData[0]?.steps;
  const processName = data?.data?.processName;
  const processId = data?.data?.processId;
  const deviceId = data?.data?.deviceId;
  let steps = beforeStep
    ? typeof beforeStep[0] === "string"
      ? JSON.parse(beforeStep)
      : beforeStep
    : [];

  const startLoop = parseInt(data?.data?.startLoop) || 0;
  const appState = useSelector(selectCurrentAppState);
  const processState = appState?.processState || {};
  const processRunningModal = appState?.processRunningModal || { visible: false, processName: "", deviceName: "", errorMessage: "" };
  const processStep = appState?.processStep || { step: "", message: "" };
  const currentNode = appState?.currentNode || "";
  const stopRunKey = processId && deviceId ? `${processId} ${deviceId}` : "";
  const stopbutton = stopRunKey ? (appState?.stopButtonByRun?.[stopRunKey] ?? "") : "";

  const [currentProcessState, setCurrentProcessState] = useState<any>({})
  const [waitingForReader, setWaitingForReader] = useState<boolean>(false);
  const [isStarting, setIsStarting] = useState(false);
  const prevStopButtonRef = useRef<string>(""); // Track previous button state to detect changes
  /** stateId from last execute/start — parent `data` often never gets it after execute. */
  const executionStateIdRef = useRef<string | undefined>(undefined);
  const executionInstanceKeyRef = useRef<string>("");
  /** Record which `processId deviceId` owns the "pausing" UI so other instances skip conflicting updates during transition. */
  const pausingUiForProcessKeyRef = useRef<string | null>(null);
  let navigate = useNavigate();
  const { currentStep, isPausedCompleted, pausedData } =
    useSelector((state: any) => state.app);
  const { user,  tenantId } = useSelector(authStateSelector);
  const [stepIndex, setStepIndex] = useState(0);
  const [stopProcess, stopInfo] = useStopActiveProcessMutation();
  const [pauseProcess, pauseProcessInfo] = usePauseProcessMutation();
  const [resumeProcess, resumeProcessInfo] = useResumeProcessMutation();
  const [executeProcess, executeInfo] = useExecuteProcessMutation();
  const [startProcess, startInfo] = useStartProcessMutation();
  const [getReferenceStock] = useLazyGetReferenceStockQuery();
  const [exitModal, setExitModal] = useState(false);
  const [pauseModal, setPauseModal] = useState<boolean>(false);
  const dispatch = useDispatch();

  const dispatchStopButton = useCallback(
    (value: string) => {
      if (processId && deviceId) {
        dispatch(setStopButtonForRun({ processId, deviceId, value }));
      }
    },
    [dispatch, processId, deviceId]
  );

  // Normalize state value for comparison (handles case variations and format differences)
  // Backend may send "PAUSE_PROCESSING" (uppercase with underscore) or "pause-processing" (lowercase with hyphen)
  const normalizeState = (state: string | undefined): string | undefined => {
    if (!state) return undefined;
    // Normalize "Active-Running" to "running" for consistency
    const normalized = state.toLowerCase().replace(/[_-]/g, '-');
    if (normalized === 'active-running') {
      return 'running';
    }
    return normalized;
  };
  
  // Helper to check if state matches (handles format variations)
  const isState = (currentState: string | undefined, targetState: string): boolean => {
    return normalizeState(currentState) === normalizeState(targetState);
  };

  useEffect(() => {
    const key = `${data?.data?.processId ?? ""}::${data?.data?.deviceId ?? ""}`;
    const fromNav =
      data?.data?.stateId ?? readStateIdFromNavRow(data?.data) ?? undefined;
    if (executionInstanceKeyRef.current !== key) {
      executionInstanceKeyRef.current = key;
      executionStateIdRef.current = fromNav;
    } else if (fromNav) {
      executionStateIdRef.current = fromNav;
    }
  }, [
    data?.data?.processId,
    data?.data?.deviceId,
    data?.data?.stateId,
    data?.data?._id,
    data?.data?.siteId,
    data?.data?.zoneId,
  ]);

  // Query to validate if process is still active (poll every 10 seconds normally, more frequently when pausing)
  const isPauseProcessing = isState(currentProcessState?.state, "pause-processing");
  const validateActiveProcess = useGetAllActiveProcessQuery(
    {
      queryParams: { page: 1, limit: 100, type: "ActiveProcess", tenantId },
      roleId: user?.roleId
    },
    {
      pollingInterval: isPauseProcessing ? 2000 : 10000, // Poll every 2 seconds when pausing, 10 seconds normally
      skip: !processId || !deviceId || !user?.roleId // Skip if missing required data
    }
  );

  /** Pause/resume require state instance id — ref, nav row (incl. Mongo _id), or active-process API row. */
  const resolveStateIdForApi = useCallback((): string | undefined => {
    if (executionStateIdRef.current) {
      return executionStateIdRef.current;
    }
    const row = data?.data;
    let sid = readStateIdFromNavRow(row);
    if (sid) {
      executionStateIdRef.current = sid;
      return sid;
    }
    const siteId = row?.siteId;
    const zoneId = row?.zoneId;
    const active = validateActiveProcess?.data?.result || [];
    const match = active.find((p: any) => {
      if (p.processId !== processId || p.deviceId !== deviceId) return false;
      if (
        siteId != null &&
        siteId !== "" &&
        p.siteId != null &&
        String(p.siteId) !== String(siteId)
      ) {
        return false;
      }
      if (
        zoneId != null &&
        zoneId !== "" &&
        p.zoneId != null &&
        String(p.zoneId) !== String(zoneId)
      ) {
        return false;
      }
      return true;
    });
    sid = readStateIdFromNavRow(match);
    if (sid) {
      executionStateIdRef.current = sid;
    }
    return sid;
  }, [data?.data, processId, deviceId, validateActiveProcess?.data?.result]);

  // Validate process state against backend - clean up if process is no longer active
  // Also detect when pause completes (pause-processing -> pause) and update state
  // Also restore button state from backend if process is already running
  useEffect(() => {
    // Reset start attempt flag on mount to avoid carrying over stale state
    isStartAttemptRef.current = false;
    // Reset resume attempt flag on mount to avoid carrying over stale state
    isResumeAttemptRef.current = false;

    const runKey = processId && deviceId ? `${processId} ${deviceId}` : "";
    console.log('[DeviceDetail] Backend sync effect running', {
      processId,
      deviceId,
      runKey,
      globalPausingKey: pausingUiForProcessKeyRef.current,
      stopbutton,
    });

    if (validateActiveProcess?.isSuccess && processId && deviceId) {
      const activeProcesses = validateActiveProcess?.data?.result || [];
      const currentProcess = activeProcesses.find(
        (p: any) => p.processId === processId && p.deviceId === deviceId
      );
      const isProcessActive = !!currentProcess;

      // Stop button is per run (`stopButtonByRun`). During a pause transition only the instance
      // that initiated pause should apply backend-driven updates so rows do not fight the same key.
      if (pausingUiForProcessKeyRef.current && runKey && pausingUiForProcessKeyRef.current !== runKey) {
        console.log('[DeviceDetail] Skipping backend-driven button restore for non-owner instance during pause transition', {
          processId,
          deviceId,
          runKey,
          globalPausingKey: pausingUiForProcessKeyRef.current,
          stopbutton,
        });
        return;
      }
      
      // Restore button state from backend if process is active
      // BUT: Don't override transition states ("pausing") - preserve them during transitions
      // BUT: Don't override if resume just succeeded (backend might not have updated yet)
      if (isProcessActive && currentProcess.isReadyForProcessing) {
        const backendState = normalizeState(currentProcess.isReadyForProcessing);
        // Also check processStates - backend might return "Active-Running" in processStates
        const backendProcessStates = normalizeState(currentProcess.processStates);
        const normalizedCurrentState = normalizeState(currentProcessState?.state);
        
        // CRITICAL: If resume just succeeded, don't override button back to "resume"
        // Backend might still show "pause" due to timing, but we just successfully resumed
        // Give backend time to update (similar to start attempt)
        // Check both: frontend state is "running" OR button is already "pause" (which we just set)
        // OR backend processStates is "running" (even if isReadyForProcessing hasn't updated yet)
        if (isResumeAttemptRef.current && (
          normalizedCurrentState === "running" || 
          stopbutton === "pause" || 
          backendProcessStates === "running"
        )) {
          console.log('[DeviceDetail] Resume just succeeded - preserving PAUSE button state, waiting for backend to update', {
            processId,
            deviceId,
            backendState,
            backendProcessStates,
            currentState: normalizedCurrentState,
            currentStopbutton: stopbutton,
            note: 'Backend may still show "pause" but resume succeeded - preserving pause button state'
          });
          // Don't override button - keep it as "pause" since resume succeeded
          // The backend will update soon and then this check will pass
          return;
        }
        
        // CRITICAL: Check backend state FIRST - if backend is "pause", update frontend state and button
        // But also check processStates - if it's "running", don't treat as paused
        const isBackendPaused = backendState === "pause" && backendProcessStates !== "running";
        // IMPORTANT: Don't let backend "pause" override an in-flight "pausing" transition.
        // The dedicated pause-completion logic below will switch the button from "pausing" to "resume"
        // once the frontend state is updated from "pause-processing" to "pause".
        if (isBackendPaused && stopbutton !== "pausing") {
          // Backend says process is paused - update frontend state if needed
          if (normalizedCurrentState === "pause-processing") {
            // Update frontend state from "pause-processing" to "pause"
            dispatch(setProcessState({
              processId,
              deviceId,
              state: "pause",
            }));
          }
          
          // Set button to "resume" if not already
          // BUT: Don't override if resume just succeeded (timing issue)
          if (stopbutton !== "resume" && !isResumeAttemptRef.current) {
            console.log('[DeviceDetail] Backend is paused - setting button to RESUME', {
              processId,
              deviceId,
              backendState,
              currentState: normalizedCurrentState,
              currentStopbutton: stopbutton
            });
            dispatchStopButton("resume");
          }
          // Don't proceed with other checks - backend pause takes priority
          return;
        }
        
        // CRITICAL: Handle pause-processing state - preserve "pausing" button state
        if (backendState === "pause-processing" || normalizedCurrentState === "pause-processing") {
          // If backend or frontend is in pause-processing, ensure button shows "pausing"
          if (stopbutton !== "pausing") {
            console.log('[DeviceDetail] Process is pause-processing - setting button to PAUSING', {
              processId,
              deviceId,
              backendState,
              currentState: normalizedCurrentState,
              currentStopbutton: stopbutton
            });
            dispatchStopButton("pausing");
          }
          // Don't proceed with other state restorations during pause transition
          return;
        }
        
        // CRITICAL: Don't restore button state if we're in a transition state
        // Preserve "pausing" state during pause transition
        if (stopbutton === "pausing") {
          // Keep "pausing" state during transition - don't restore from backend
          console.log('[DeviceDetail] Preserving PAUSING button state during transition', {
            processId,
            deviceId,
            backendState,
            currentState: normalizedCurrentState
          });
          return;
        }
        
        // CRITICAL: If backend state is "ready", keep button as "start" - process hasn't been started yet
        // Even if processStates is "running", if isReadyForProcessing is "ready", the process is waiting for user to click start
        if (backendState === "ready") {
          // Process is in READY state - button should be "start" until user actually starts it
          if (stopbutton !== "start" && stopbutton !== "") {
            console.log('[DeviceDetail] Process is in READY state - keeping button as START', {
              processId,
              deviceId,
              backendState,
              backendProcessStates,
              currentStopbutton: stopbutton,
              note: 'Process is ready but not started - button should be START, not PAUSE'
            });
            dispatchStopButton("start");
          }
          return; // Don't proceed with other checks - ready state takes priority
        }
        
        // If backend says process is running/resume, set button to "pause"
        // Check both isReadyForProcessing and processStates
        // BUT: Only if backendState is NOT "ready" (checked above)
        const isBackendRunning = backendState === "start" || backendState === "resume" || backendState === "running" || 
                                 (backendProcessStates === "running" && backendState !== "ready");
        if (isBackendRunning) {
          if (stopbutton !== "pause") {
            console.log('[DeviceDetail] Restoring button state to PAUSE from backend', {
              processId,
              deviceId,
              backendState,
              backendProcessStates,
              currentStopbutton: stopbutton
            });
            dispatchStopButton("pause");
          }
        }
      } else if (!isProcessActive && stopbutton !== "start" && stopbutton !== "") {
        // If process is not active, reset button to "start"
        console.log('[DeviceDetail] Process not active - resetting button to START', {
          processId,
          deviceId,
          currentStopbutton: stopbutton
        });
        dispatchStopButton("start");
      }

      // CRITICAL: Detect when pause completes - backend state changed from pause-processing to pause
      // Update Redux state to reflect the actual backend state
      if (currentProcess && currentProcess.isReadyForProcessing) {
        const backendState = currentProcess.isReadyForProcessing;
        const normalizedBackendState = normalizeState(backendState);
        const normalizedCurrentState = normalizeState(currentProcessState?.state);
        
        // If backend says "pause" but frontend still thinks "pause-processing", update it
        if (normalizedBackendState === "pause" && normalizedCurrentState === "pause-processing") {
          console.log('[Device Detail] Pause completed - updating state from pause-processing to pause', {
            processId,
            deviceId,
            backendState,
            currentState: currentProcessState?.state
          });
          
          dispatch(setProcessState({
            processId,
            deviceId,
            state: "pause",
          }));
          
          // Update button to "resume" when pause completes
          pausingUiForProcessKeyRef.current = null;
          dispatchStopButton("resume");
        }
        
        // Also check if backend is "pause" and button is still "pausing" (catch any missed transitions)
        if (normalizedBackendState === "pause" && stopbutton === "pausing") {
          console.log('[Device Detail] Backend is paused but button still showing pausing - updating to resume', {
            processId,
            deviceId,
            backendState,
            currentStopbutton: stopbutton
          });
          
          dispatch(setProcessState({
            processId,
            deviceId,
            state: "pause",
          }));
          
          // Update button to "resume" when pause completes
          pausingUiForProcessKeyRef.current = null;
          dispatchStopButton("resume");
        }
      }

      // If process is now active and we were waiting for it (start attempt), clear the flag early
      // This means the backend has successfully added it to the active list
      if (isProcessActive && isStartAttemptRef.current) {
        console.log('[Device Detail] Process is now active - clearing start attempt flag early', {
          processId,
          deviceId
        });
        isStartAttemptRef.current = false;
      }
      
      // If process is now active and backend state matches frontend "running" state, clear resume attempt flag
      // This means the backend has successfully updated after resume
      if (isProcessActive && currentProcess && currentProcess.isReadyForProcessing) {
        const backendState = normalizeState(currentProcess.isReadyForProcessing);
        const normalizedCurrentState = normalizeState(currentProcessState?.state);
        
        // CRITICAL: If resume just succeeded, ensure state stays as "running" (not "active-running" or any other value)
        // Backend might return different state values, but we want to keep it as "running"
        if (isResumeAttemptRef.current) {
          // Also check processStates from backend
          const backendProcessStates = normalizeState(currentProcess.processStates);
          
          // If state is not "running", update it to "running"
          if (normalizedCurrentState !== "running") {
            console.log('[Device Detail] Resume succeeded - ensuring state is "running" (not "' + currentProcessState?.state + '")', {
              processId,
              deviceId,
              backendState,
              backendProcessStates,
              currentState: currentProcessState?.state,
              normalizedCurrentState
            });
            dispatch(setProcessState({
              processId,
              deviceId,
              state: "running",
            }));
          }
          
          // Clear resume attempt flag when backend state indicates process is running
          // Check both isReadyForProcessing and processStates
          const isBackendRunning = backendState === "start" || backendState === "resume" || backendState === "running" || 
                                   backendProcessStates === "running";
          if (isBackendRunning) {
          console.log('[Device Detail] Backend state updated after resume - clearing resume attempt flag', {
            processId,
            deviceId,
            backendState,
              backendProcessStates,
            currentState: normalizedCurrentState
          });
          isResumeAttemptRef.current = false;
          }
        }
      }

      // If process is not in active list but we think it's running, clean up state.
      // BUT:
      // - Skip cleanup if we just started the process (race condition - backend might not have updated active list yet)
      // - Skip cleanup if we're still receiving workflow state messages (process is clearly active)
      // Give it 6 seconds after start attempt AND at least 10 seconds after last workflow state
      // before allowing cleanup.
      const shouldSkipCleanup = isStartAttemptRef.current;
      const lastWorkflowStateTime = lastWorkflowStateTimeRef.current;
      const now = Date.now();
      const timeSinceLastWorkflowState = lastWorkflowStateTime ? now - lastWorkflowStateTime : null;
      const hasRecentWorkflowActivity = timeSinceLastWorkflowState !== null && timeSinceLastWorkflowState < 10000; // 10 seconds
      
      // Also check if startInfo indicates we just successfully started (within last 8 seconds)
      // This prevents showing "Process is no longer running" right after "Process started successfully"
      const justStartedSuccessfully = startInfo?.isSuccess && startInfo?.fulfilledTimeStamp && 
        (Date.now() - startInfo.fulfilledTimeStamp) < 8000; // Within 8 seconds of successful start
      
      if (!isProcessActive && !shouldSkipCleanup && !justStartedSuccessfully && !hasRecentWorkflowActivity && (currentProcessState?.state === "running" || 
      currentProcessState?.state === "pause" ||
      currentProcessState?.state === "resume" ||
      data?.data?.isReadyForProcessing === "start" || 
          data?.data?.isReadyForProcessing === "resume")) {
        console.log('[Device Detail] Process no longer active - cleaning up state:', {
          processId,
          deviceId,
          currentState: currentProcessState?.state,
          isReadyForProcessing: data?.data?.isReadyForProcessing
        });

        // Clean up Redux state
        dispatch(removeProcessState({ processId, deviceId }));
        dispatch(removeCurrentStep({ processId, deviceId }));
        dispatch(setProcessState({
          processId,
          deviceId,
          state: "stop",
        }));
        dispatchStopButton("start");
        
        message.info({
          content: "Process is no longer running.",
          key: "process-state-cleanup",
          duration: 3
        });
      } else if (!isProcessActive && (shouldSkipCleanup || justStartedSuccessfully)) {
        console.log('[Device Detail] Skipping cleanup - process was just started, waiting for backend to update active list', {
          processId,
          deviceId,
          currentState: currentProcessState?.state,
          shouldSkipCleanup,
          justStartedSuccessfully,
          startInfoFulfilledTime: startInfo?.fulfilledTimeStamp,
          timeSinceStart: startInfo?.fulfilledTimeStamp ? Date.now() - startInfo.fulfilledTimeStamp : 'N/A'
        });
      }
    }
  }, [validateActiveProcess?.isSuccess, validateActiveProcess?.data?.result, processId, deviceId, currentProcessState?.state, data?.data?.isReadyForProcessing, stopbutton, dispatch, dispatchStopButton]);

  useEffect(() => {
    const newState = processState[`${data?.data?.processId} ${data?.data?.deviceId}`];
    setCurrentProcessState(newState);
    currentProcessStateRef.current = newState; // Update ref for WebSocket handlers
  }, [processState, data?.data?.processId, data?.data?.deviceId]);

  // Notify parent component when process state changes
  useEffect(() => {
    const currentState = currentProcessState?.state;
    const isReadyForProcessing = data?.data?.isReadyForProcessing;
    
    // Treat pause-processing as pause state (not running) for state tracking
    // But still consider it as active process (so callback works correctly)
    const isProcessRunning = 
      isState(isReadyForProcessing, "pause") ||
      isState(currentState, "pause") ||
      isState(currentState, "pause-processing") || // Treated as pause, but still active
      isState(currentState, "resume") ||
      isState(currentState, "running") ||
      isState(isReadyForProcessing, "resume") ||
      isState(isReadyForProcessing, "start") ||
      isState(isReadyForProcessing, "pause-processing"); // Treated as pause, but still active

    if (onProcessStateChange) {
      onProcessStateChange(isProcessRunning);
    }
  }, [currentProcessState, data?.data?.isReadyForProcessing, onProcessStateChange])

  // Removed hardcoded step progression - now using real-time WebSocket updates
  // The StateChange WebSocket listener handles step progression based on actual backend process state

  useEffect(() => {
    let step = currentStep[`${data?.data?.processId} ${data?.data?.deviceId}`]
    if (step !== undefined && step !== null) {
      setStepIndex(step);
      console.log(`Initialized stepIndex from Redux: ${step}`);
    }
  }, [currentStep, data?.data?.processId, data?.data?.deviceId]);

  const stepProcess: any = {
    "Scan Tag": scanTag,
    "GPIO Light": gpio,
    "GPIO Sound": gpioSound,
    "Print Label": printLabel,
    "Scan Multiple": compareIcon,
    "Data Sync": dataSyncIcon,
    Notify: notifyIcon,
    Wait: waitIcon,
  };
  // Container width for precise connector alignment
  const stepsWrapperRef = useRef<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = stepsWrapperRef?.current?.offsetWidth || 0;
      setContainerWidth(width);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);


  // Get user's devices to filter device status (same approach as Device Status page)
  const deviceDataQuery = useGetAllDeviceOfUserQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !user?.userId
  });

  // Use the same API as Device Status page - get all device statuses, then filter by MAC
  const getDeviceStatus = useGetDeviceStatusQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true, 
    pollingInterval: 5000,
    skip: false // Never skip - always try to fetch device status
  });

  // Get device MAC address from device data query if not available in data.data
  const deviceMacAddress = useMemo(() => {
    // First try to get from data.data
    let mac = data?.data?.mac || data?.data?.macAddress;
    
    // If not found, try to get from deviceDataQuery by matching deviceId
    if (!mac && data?.data?.deviceId && deviceDataQuery?.data?.result?.deviceData) {
      const device = deviceDataQuery.data.result.deviceData.find((d: any) => d.deviceId === data.data.deviceId);
      mac = device?.mac || device?.macAddress;
    }
    
    // If still not found, try to extract MAC address from device name (format: "DeviceName - [MAC]")
    if (!mac && data?.data?.deviceName) {
      const macMatch = data.data.deviceName.match(/\[([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5})\]/i);
      if (macMatch && macMatch[1]) {
        mac = macMatch[1];
        console.log('[Device Detail] Extracted MAC address from device name', {
          deviceName: data.data.deviceName,
          extractedMac: mac,
          deviceId: data?.data?.deviceId
        });
      }
    }
    
    return mac?.toUpperCase()?.trim();
  }, [data?.data?.mac, data?.data?.macAddress, data?.data?.deviceId, data?.data?.deviceName, deviceDataQuery?.data?.result?.deviceData]);

  // Filter API polling data by the specific device's MAC address (same pattern as Device Status page)
  const apiDeviceStatusFiltered = useMemo(() => {
    if (!deviceMacAddress) {
      return [];
    }
    
    const apiStatus = getDeviceStatus?.data?.result || [];
    
    // Filter for the specific device's MAC address
    const targetKey = normalizeMacKey(deviceMacAddress);
    const filtered = apiStatus.filter((deviceStatus: any) => {
      const statusMac = deviceStatus?.deviceMac;
      return statusMac && normalizeMacKey(statusMac) === targetKey;
    });
    
    return filtered;
  }, [getDeviceStatus?.data?.result, deviceMacAddress]);

  // Log API polling status for device status
  useEffect(() => {
    if (getDeviceStatus?.isError) {
      console.error('[Device Detail] Error fetching device status via API', {
        error: getDeviceStatus?.error,
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        deviceMac: deviceMacAddress,
        timestamp: new Date().toISOString()
      });
    }
    if (getDeviceStatus?.isSuccess) {
      console.log('[Device Detail] Device status fetched successfully via API', {
        totalStatusCount: getDeviceStatus?.data?.result?.length || 0,
        filteredStatusCount: apiDeviceStatusFiltered.length,
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        deviceMac: deviceMacAddress,
        deviceMacSource: data?.data?.mac || data?.data?.macAddress ? 'data.data' : (deviceDataQuery?.data?.result?.deviceData ? 'deviceDataQuery' : 'not found'),
        deviceStatus: apiDeviceStatusFiltered[0] ? {
          deviceMac: apiDeviceStatusFiltered[0]?.deviceMac,
          status: apiDeviceStatusFiltered[0]?.status
        } : null,
        timestamp: new Date().toISOString()
      });
    }
  }, [getDeviceStatus?.isError, getDeviceStatus?.isSuccess, getDeviceStatus?.data?.result, apiDeviceStatusFiltered, data?.data?.mac, data?.data?.macAddress, user?.userId]);

  // WebSocket state for device status (same pattern as Device Status page and Activity Logs main component)
  const [wsDeviceStatus, setWsDeviceStatus] = useState<any[] | null>(null);

  // WebSocket listener for device status updates (filtered by MAC address - same pattern as Device Status page)
  useEffect(() => {
    if (!user?.userId || !deviceMacAddress) {
      console.log('[Device Detail] WebSocket listener skipped - missing userId or device MAC', {
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        macAddress: data?.data?.macAddress,
        mac: data?.data?.mac,
        deviceMacAddress: deviceMacAddress,
        deviceDataQueryLoaded: !!deviceDataQuery?.data?.result?.deviceData,
        timestamp: new Date().toISOString()
      });
      return;
    }
    // Use the resolved device MAC address
    const deviceMac = deviceMacAddress;
    const userSpecificEventName = `${WebsocketKey.DEVICE_STATUS_UPDATE}-${user.userId}`;
    const generalEventName = WebsocketKey.DEVICE_STATUS_UPDATE;

    console.log('[Device Detail] Registering WebSocket listeners for device status', {
      userId: user?.userId,
      deviceMac,
      originalMacAddress: data?.data?.macAddress,
      originalMac: data?.data?.mac,
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    const handleDeviceStatusUpdate = (wsData: any) => {
      console.log('[Device Detail] WebSocket device status update received', {
        userId: user?.userId,
        deviceMac,
        eventName: userSpecificEventName,
        wsDataKeys: Object.keys(wsData || {}),
        hasResult: !!wsData?.result,
        hasDataResult: !!wsData?.data?.result,
        timestamp: new Date().toISOString()
      });

      let result: any[] = [];
      if (Array.isArray(wsData?.result)) {
        result = wsData.result;
      } else if (Array.isArray(wsData?.data?.result)) {
        result = wsData.data.result;
      } else if (Array.isArray(wsData?.data)) {
        result = wsData.data;
      } else if (Array.isArray(wsData)) {
        result = wsData;
      } else if (wsData?.data && typeof wsData.data === "object") {
        if (Array.isArray(wsData.data.deviceStatus)) {
          result = wsData.data.deviceStatus;
        } else if (Array.isArray(wsData.data.status)) {
          result = wsData.data.status;
        } else if (Array.isArray(wsData.data.devices)) {
          result = wsData.data.devices;
        }
      }
      if (Array.isArray(result)) {
        console.log('[Device Detail] Processing WebSocket device status array', {
          totalCount: result.length,
          deviceMacs: result.map((d: any) => d?.deviceMac),
          targetDeviceMac: deviceMac,
          timestamp: new Date().toISOString()
        });

        // Filter for the specific device's MAC address (normalized comparison - same as Device Status page)
        const targetKey = normalizeMacKey(deviceMac);
        const deviceSpecificStatus = result.filter((d: any) => {
          const statusMac = d?.deviceMac;
          const matches = statusMac && normalizeMacKey(statusMac) === targetKey;
          if (matches) {
            console.log('[Device Detail] Device status match found', {
              statusMac,
              targetMac: deviceMac,
              deviceStatus: d?.status,
              timestamp: new Date().toISOString()
            });
          }
          return matches;
        });
        
        console.log('[Device Detail] Filtered device status', {
          beforeFilter: result.length,
          afterFilter: deviceSpecificStatus.length,
          matched: deviceSpecificStatus.length > 0,
          deviceStatus: deviceSpecificStatus[0] ? {
            deviceMac: deviceSpecificStatus[0]?.deviceMac,
            status: deviceSpecificStatus[0]?.status
          } : null,
          timestamp: new Date().toISOString()
        });
        
        if (deviceSpecificStatus.length > 0) {
          setWsDeviceStatus(deviceSpecificStatus);
        } else if (result.length > 0) {
          setWsDeviceStatus(null);
        }
      } else {
        console.warn('[Device Detail] WebSocket data structure unexpected', {
          wsData,
          timestamp: new Date().toISOString()
        });
      }
    };

    socket.on(userSpecificEventName, handleDeviceStatusUpdate);
    socket.on(generalEventName, handleDeviceStatusUpdate);

    console.log('[Device Detail] WebSocket listeners registered for device status', {
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    return () => {
      console.log('[Device Detail] Cleaning up WebSocket listeners for device status', {
        userSpecificEventName,
        generalEventName,
        timestamp: new Date().toISOString()
      });
      socket.off(userSpecificEventName, handleDeviceStatusUpdate);
      socket.off(generalEventName, handleDeviceStatusUpdate);
    };
  }, [user?.userId, deviceMacAddress]);

  // Prefer polling API when present (aligns with backend CRS); WS is often partial (other readers).
  const mergedDeviceStatus = useMemo(() => {
    const api = apiDeviceStatusFiltered?.[0];
    const ws = wsDeviceStatus?.[0];
    if (api) return [api];
    if (ws) return [ws];
    return [];
  }, [wsDeviceStatus, apiDeviceStatusFiltered]);
  
  // Memoize device status to only update when status actually changes
  const deviceStatusDisplay = useMemo(() => {
    const status = mergedDeviceStatus[0]?.status;
    const normalizedStatus = normalizeStatus(status);
    
    // Explicitly check for offline/disconnected statuses
    const isOffline = isOfflineStatus(status);
    
    // Check for online statuses
    const isOnline = !isOffline && (
                    normalizedStatus === "connected" || 
                    normalizedStatus === "running" ||
                    normalizedStatus === "active-running" ||
                    normalizedStatus === "active" ||
                    status === "connected" || 
                    status === "running"
    );
    
    return {
      status: status || "unknown",
      isOnline,
      displayText: isOnline ? "Online" : "Offline", // Always show "Offline" for disconnected/offline statuses
      color: isOnline ? '#52c41a' : '#ff4d4f'
    };
  }, [mergedDeviceStatus[0]?.status]); // Only re-compute when status changes
  
  // Log merged device status
  useEffect(() => {
    console.log('[Device Detail] Merged device status', {
      wsDeviceStatusCount: wsDeviceStatus?.length || 0,
      apiDeviceStatusCount: apiDeviceStatusFiltered?.length || 0,
      mergedCount: mergedDeviceStatus.length,
      dataSource: apiDeviceStatusFiltered?.length ? 'API (preferred)' : (wsDeviceStatus?.length ? 'WebSocket' : 'None'),
      deviceStatus: mergedDeviceStatus[0] ? {
        deviceMac: mergedDeviceStatus[0]?.deviceMac,
        status: mergedDeviceStatus[0]?.status,
        displayStatus: deviceStatusDisplay.displayText,
        isOnline: deviceStatusDisplay.isOnline
      } : null,
      targetDeviceMac: data?.data?.mac || data?.data?.macAddress,
      apiQueryStatus: {
        isLoading: getDeviceStatus?.isLoading,
        isError: getDeviceStatus?.isError,
        isSuccess: getDeviceStatus?.isSuccess,
        isSkipped: getDeviceStatus?.isUninitialized,
        error: getDeviceStatus?.error
      },
      apiDeviceStatusData: getDeviceStatus?.data?.result,
      filteredApiDeviceStatusData: apiDeviceStatusFiltered,
      timestamp: new Date().toISOString()
    });
      }, [wsDeviceStatus, apiDeviceStatusFiltered, mergedDeviceStatus, getDeviceStatus?.isLoading, getDeviceStatus?.isError, getDeviceStatus?.isSuccess, getDeviceStatus?.isUninitialized, getDeviceStatus?.error, getDeviceStatus?.data?.result, deviceMacAddress, deviceStatusDisplay]);

  const onStartClick = async () => {
    console.log('========================================');
    console.log('[PROCESS START] Method: START BUTTON');
    console.log('========================================');
    console.log('DeviceDetail: onStartClick called', {
      method: 'START_BUTTON',
      userId: user?.userId,
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      processName: data?.data?.processName || data?.data?.name,
      tenantId: data?.data?.tenantId,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      roleId: user?.roleId,
      isPredefined: true,
      timestamp: new Date().toISOString(),
      flags: {
        startedViaStartButton: true,
        startedViaApplyButton: false,
        startedViaEditReferenceStockButton: false
      }
    });

    // Note: We allow the process to start even if device is offline
    // The process will move to Wait node after starting and continue automatically when device reconnects

    setIsStarting(true);
    isStartAttemptRef.current = true; // Track that start button was clicked
    
    // Track if device is offline at start - we'll handle this after process starts
    const deviceStatusAtStart = mergedDeviceStatus?.[0]?.status;
    const isDeviceOfflineAtStart = deviceStatusAtStart !== "connected" && deviceStatusAtStart !== "running";
    
    // Get steps for Wait node lookup
    let beforeStep = data?.data?.steps
      ? data?.data?.steps
      : data?.data?.processData?.[0]?.steps;
    let steps = beforeStep
      ? typeof beforeStep[0] === "string"
        ? JSON.parse(beforeStep)
        : beforeStep
      : [];
    
    try {
      // Check if this is Check Reference Stock process - validate reference stock exists
      const currentProcessName = data?.data?.processName || data?.data?.name;
      if (currentProcessName === ProcessName.CHECK_STOCK) {
        console.log('DeviceDetail: Check Reference Stock process detected - validating reference stock exists');
        try {
          const referenceStockResult = await getReferenceStock({
            processId: data?.data?.processId,
            siteId: data?.data?.siteId,
            zoneId: data?.data?.zoneId
          }).unwrap();
          
          // Check if reference stock exists (has tags or data)
          // More strict check: must have actual tag data, not just empty arrays or objects
          const tags = referenceStockResult?.result?.tags || referenceStockResult?.result?.data || [];
          const hasTags = Array.isArray(tags) && tags.length > 0;
          const hasData = referenceStockResult?.result && 
                         typeof referenceStockResult.result === 'object' && 
                         Object.keys(referenceStockResult.result).length > 0 &&
                         !Array.isArray(referenceStockResult.result);
          
          const hasReferenceStock = hasTags || (hasData && !Array.isArray(referenceStockResult.result));
          
          if (!hasReferenceStock) {
            const errorMessage = "Please set the reference stock before starting the process.";
            console.error('DeviceDetail: Reference stock is empty - blocking process start', {
              zoneId: data?.data?.zoneId,
              siteId: data?.data?.siteId,
              processId: data?.data?.processId,
              referenceStockResult: referenceStockResult
            });
            
            message.error({
              content: errorMessage,
              key: "reference-stock-empty",
              duration: 5
            });
            
            dispatch(setProcessRunningModal({
              visible: true,
              processName: data?.data?.processName || "Check Reference Stock",
              deviceName: data?.data?.deviceName || "Device",
              errorMessage: errorMessage
            }));
            
            setIsStarting(false);
            isStartAttemptRef.current = false; // Reset start attempt flag
            return; // Block process from starting
          }
          
          console.log('DeviceDetail: Reference stock validation passed', {
            referenceStockResult: referenceStockResult
          });
        } catch (refStockError: any) {
          // If API returns 404 or error, assume reference stock doesn't exist
          const status = refStockError?.status || refStockError?.originalStatus || refStockError?.data?.statusCode;
          if (status === 404 || refStockError?.data?.error?.message?.toLowerCase().includes('not found')) {
            const errorMessage = "Please set the reference stock before starting the process.";
            console.error('DeviceDetail: Reference stock not found for zone - blocking process start', {
              zoneId: data?.data?.zoneId,
              siteId: data?.data?.siteId,
              processId: data?.data?.processId,
              error: refStockError
            });
            
            message.error({
              content: errorMessage,
              key: "reference-stock-empty",
              duration: 5
            });
            
            dispatch(setProcessRunningModal({
              visible: true,
              processName: data?.data?.processName || "Check Reference Stock",
              deviceName: data?.data?.deviceName || "Device",
              errorMessage: errorMessage
            }));
            setIsStarting(false);
            isStartAttemptRef.current = false; // Reset start attempt flag
            return;
          }
          // For other errors, log but continue (might be network issue)
          console.warn('DeviceDetail: Error checking reference stock, continuing anyway', {
            error: refStockError
          });
        }
      }

      // Check if process is already in "ready" state (started via Apply/Edit Reference Stock button)
      // If so, skip executeProcess and only call startProcess
      const isProcessReady = data?.data?.isReadyForProcessing === "ready";
      
      // Determine if this is Check Reference Stock process (used for both executeProcess and startProcess)
      const isCheckReferenceStock = currentProcessName === ProcessName.CHECK_STOCK;
      
      let resolvedStateId =
        data?.data?.stateId ?? readStateIdFromNavRow(data?.data) ?? executionStateIdRef.current;

      if (!isProcessReady) {
        
        const executePayload = {
          userId: user?.userId,
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          tenantId: data?.data?.tenantId || tenantId,
          siteId: data?.data?.siteId,
          zoneId: data?.data?.zoneId,
          roleId: user?.roleId,
          isPredefined: data?.data?.isPredefined !== undefined ? data?.data?.isPredefined : true, // Check Reference Stock is predefined
          workflowName: "Check Reference Stock", // Explicitly set to ensure only CRS workflow is initialized
          workflowNameForStorage: data?.data?.processName || "Check Reference Stock",
          startedViaStartButton: isCheckReferenceStock // Flag to allow overwriting processes started via Apply button
        };
        
        console.log('[PROCESS START] START BUTTON - Calling executeProcess API', {
          method: 'START_BUTTON',
          apiEndpoint: 'POST /api/v1/process/execute',
          payload: executePayload,
          flags: {
            startedViaStartButton: executePayload.startedViaStartButton,
            startedViaApplyButton: false,
            startedViaEditReferenceStockButton: false
          },
          timestamp: new Date().toISOString()
        });
        
        const executeResult = await executeProcess(executePayload).unwrap();
        resolvedStateId = readStateIdFromApiPayload(executeResult) || resolvedStateId;
        console.log('[PROCESS START] START BUTTON - executeProcess API success', {
          method: 'START_BUTTON',
          response: executeResult,
          timestamp: new Date().toISOString()
        });

        // Small delay to ensure state is fully committed to database
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.log('DeviceDetail: Process is already in "ready" state (started via Apply/Edit Reference Stock button) - skipping executeProcess, will only call startProcess');
      }

      // After leaving Activity Logs and returning, nav row often has no stateId; backend still has the row on active process list.
      if (!resolvedStateId) {
        resolvedStateId = resolveStateIdForApi();
      }
      if (!resolvedStateId && isProcessReady) {
        try {
          const refetchResult = await validateActiveProcess.refetch();
          const freshList =
            (refetchResult as unknown as { data?: { result?: any[] } })?.data?.result ?? [];
          const row = data?.data;
          const siteId = row?.siteId;
          const zoneId = row?.zoneId;
          const match = freshList.find((p: any) => {
            if (p.processId !== processId || p.deviceId !== deviceId) return false;
            if (
              siteId != null &&
              siteId !== "" &&
              p.siteId != null &&
              String(p.siteId) !== String(siteId)
            ) {
              return false;
            }
            if (
              zoneId != null &&
              zoneId !== "" &&
              p.zoneId != null &&
              String(p.zoneId) !== String(zoneId)
            ) {
              return false;
            }
            return true;
          });
          resolvedStateId = readStateIdFromNavRow(match);
        } catch (e) {
          console.warn("[DeviceDetail] refetch active processes for stateId failed", e);
        }
      }

      if (resolvedStateId) {
        executionStateIdRef.current = resolvedStateId;
      }

      if (!resolvedStateId) {
        message.error("stateId is required to start process. Please execute the process again.");
        setIsStarting(false);
        isStartAttemptRef.current = false;
        return;
      }

      // Step 2: Start Process (emits event to workflow)
      // CRITICAL: Set startedViaStartButton flag ONLY when Start button for Check Reference Stock is pressed
      // This flag ensures notifications and cycles are NOT skipped for Check Reference Stock workflows
      // Explicitly set to true for Check Reference Stock Start button, false otherwise
      const startPayload = {
        userId: user?.userId,
        stateId: resolvedStateId,
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        startedViaStartButton: isCheckReferenceStock ? true : false, // Explicitly true for Check Reference Stock Start button
        startedViaApplyButton: false, // Explicitly false when Start button is pressed
        startedViaEditReferenceStockButton: false, // Explicitly false when Start button is pressed
      };
      
      // Small delay to ensure state is fully committed to database
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('[PROCESS START] START BUTTON - Calling startProcess API', {
        method: 'START_BUTTON',
        apiEndpoint: 'POST /api/v1/process/start',
        payload: startPayload,
        isCheckReferenceStock,
        flags: {
          startedViaStartButton: startPayload.startedViaStartButton,
          startedViaApplyButton: false,
          startedViaEditReferenceStockButton: false
        },
        note: 'startedViaStartButton is set to true ONLY when Start button for Check Reference Stock is pressed',
        backendNote: 'Backend will find state with processId: ' + data?.data?.processId + ' and deviceId: ' + data?.data?.deviceId,
        timestamp: new Date().toISOString()
      });
      
      const startResult = await startProcess(startPayload).unwrap();
      const stateIdFromStart = readStateIdFromApiPayload(startResult);
      if (stateIdFromStart) {
        executionStateIdRef.current = stateIdFromStart;
      }
      console.log('[PROCESS START] START BUTTON - startProcess API success', {
        method: 'START_BUTTON',
        response: startResult,
        timestamp: new Date().toISOString()
      });

      // Keep isStartAttemptRef true for 6 seconds to prevent premature cleanup
      // This gives the backend time to update the active process list after starting
      // Extended from 3 to 6 seconds to handle cases where backend takes longer to update
      setTimeout(() => {
        isStartAttemptRef.current = false;
        console.log('DeviceDetail: Start attempt flag cleared after 6 seconds');
      }, 6000);

      // If device was offline at start, move to Wait node and send notification
      if (isDeviceOfflineAtStart) {
        const currentProcessName = data?.data?.processName || data?.data?.name;
        const isCheckReferenceStock = currentProcessName === ProcessName.CHECK_STOCK;
        
        // CRITICAL: Set process state to "wait" and update button to "pause" so UI shows process is running
        dispatch(setProcessState({
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          state: "wait",
        }));
        dispatchStopButton("pause"); // Change button from "start" to "pause"
        setWaitingForReader(true);
        
        // Move to Wait node
        const normalize = (str: string) => str?.toLowerCase().replace(/[\s_-]/g, '') || '';
        const waitStepIndex = steps.findIndex((step: any) => {
          const stepName = step?.name || step;
          return normalize(stepName) === 'wait';
        });
        
        if (waitStepIndex !== -1) {
          setStepIndex(waitStepIndex);
          dispatch(setCurrentStep({
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            step: waitStepIndex
          }));
          dispatch(setCurrentNode('Wait'));
          console.log('DeviceDetail: Process started but device is offline - moved to WAIT node, will auto-resume when device reconnects');
        }
        
        // Check active process for flags to determine how the workflow was started
        // Only send notification if started via Start button (not via Apply or Edit Reference Stock)
        const activeProcesses = validateActiveProcess?.data?.result || [];
        const currentProcess = activeProcesses.find(
          (p: any) => p.processId === data?.data?.processId && p.deviceId === data?.data?.deviceId
        );
        
        // Extract flags from active process state
        const startedViaStartButton = currentProcess?.startedViaStartButton === true;
        const startedViaApplyButton = currentProcess?.startedViaApplyButton === true;
        const startedViaEditReferenceStockButton = currentProcess?.startedViaEditReferenceStockButton === true;
        
        // Only send notification if Check Reference Stock AND started via Start button
        const shouldSendInitialNotification = isCheckReferenceStock && 
          startedViaStartButton === true && 
          startedViaApplyButton !== true && 
          startedViaEditReferenceStockButton !== true;
        
        if (shouldSendInitialNotification) {
          const notificationEventName = `${WebsocketKey.Notification_error}-${data?.data?.deviceId}-${data?.data?.processId}`;
          const notificationPayload = {
            deviceId: data?.data?.deviceId,
            processId: data?.data?.processId,
            userId: user?.userId,
            deviceName: data?.data?.deviceName || 'Device',
            processName: data?.data?.processName || 'Check Reference Stock',
            error: {
              message: `Process has been started but device is disconnected or offline. Please check the reader and restart the reader. Process will continue once device is reconnected.`,
              type: 'DEVICE_OFFLINE',
              timestamp: new Date().toISOString()
            },
            data: {
              message: `Process has been started but device is disconnected or offline. Please check the reader and restart the reader. Process will continue once device is reconnected.`,
              deviceStatus: deviceStatusAtStart,
              isInitialNotification: true
            }
          };
          
          socket.emit(notificationEventName, notificationPayload);
          lastDeviceOfflineNotificationRef.current = Date.now();
          console.log('DeviceDetail: Sent initial device offline notification', {
            eventName: notificationEventName,
            payload: notificationPayload,
            startedViaStartButton,
            startedViaApplyButton,
            startedViaEditReferenceStockButton,
            note: 'Notification sent because process was started via Start button'
          });
        } else {
          console.log('DeviceDetail: Skipping initial device offline notification', {
            isCheckReferenceStock,
            startedViaStartButton,
            startedViaApplyButton,
            startedViaEditReferenceStockButton,
            note: 'Notifications are skipped for processes started via Apply or Edit Reference Stock buttons'
          });
        }
        
        // Show user-friendly info message (no error modal - just info)
        message.info({ 
          content: `Process started. Waiting for device "${data?.data?.deviceName || 'device'}" to reconnect. The process will continue automatically once the device is online.`,
          key: "device-offline-wait",
          duration: 5 
        });
      } else {
        // Device is online - normal flow
        dispatch(setProcessState({
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          state: "running",
        }));
        dispatchStopButton("pause");
        message.success({ 
          content: "Process started successfully", 
          key: "notificationKey", 
          className: "successMessage", 
          duration: 2 
        })
        setWaitingForReader(false);
      }
    } catch (err: any) {
      const status = err?.status || err?.originalStatus || err?.data?.statusCode;
      let msg = err?.data?.error?.message || err?.data?.message || err?.message || String(err);
      
      // Handle HTML error responses (like 504 Gateway Timeout from nginx)
      // Check if the error message or error data is HTML
      const errorDataString = typeof err?.data === 'string' ? err.data : '';
      const isHtmlError = (typeof msg === 'string' && (msg.trim().startsWith('<html') || msg.includes('Gateway Time-out') || msg.includes('504'))) ||
                         (typeof errorDataString === 'string' && (errorDataString.trim().startsWith('<html') || errorDataString.includes('Gateway Time-out')));
      
      if (isHtmlError) {
        // Extract status code from HTML if available
        const htmlContent = typeof msg === 'string' ? msg : errorDataString;
        const statusMatch = htmlContent.match(/<title>(\d+)\s+([^<]+)<\/title>/i);
        if (statusMatch) {
          const htmlStatus = statusMatch[1];
          const htmlError = statusMatch[2];
          if (htmlStatus === '504' || htmlError?.toLowerCase().includes('gateway time-out')) {
            msg = "The request timed out. The server may be temporarily unavailable. Please try again in a few moments.";
          } else {
            msg = `Server error (${htmlStatus}): ${htmlError || 'The server encountered an error'}. Please try again later.`;
          }
        } else {
          // Generic HTML error - provide user-friendly message
          msg = "The server is temporarily unavailable. Please try again in a few moments.";
        }
      }
      
      console.error('DeviceDetail: start/execute error', { 
        status, 
        msg, 
        errorData: err?.data,
        fullError: err,
        errorType: err?.error?.status,
        requestUrl: err?.meta?.request?.url,
        requestMethod: err?.meta?.request?.method
      });
      
      // Log if request was even attempted
      if (err?.meta) {
        console.error('DeviceDetail: Request metadata:', err.meta);
      } else {
        console.error('DeviceDetail: No request metadata - request may not have been sent');
      }

      // Handle offline reader 400 - show clear error message and prevent starting
      if (status === 400 && (msg?.toLowerCase?.().includes('offline') || msg?.toLowerCase?.().includes('disconnected') || msg?.toLowerCase?.().includes('unable to connect'))) {
        // Only show error once
        if (!hasShownConnectionErrorRef.current) {
          hasShownConnectionErrorRef.current = true;
          
          // Show clear error message in modal instead of just toast
          dispatch(setProcessRunningModal({
            visible: true,
            processName: data?.data?.processName || "Check Reference Stock",
            deviceName: data?.data?.deviceName || "Device",
            errorMessage: `Cannot start the process. The device "${data?.data?.deviceName || 'device'}" is currently offline or disconnected. Please ensure the device is connected and try again.`
          }));
          
          message.error({ 
            content: 'Device is offline or disconnected. Cannot start the process.',
            key: "device-connection-error",
            duration: 5 
          });
        }
        
        setIsStarting(false);
        isStartAttemptRef.current = false;
        
        // Don't move to Wait node if we're trying to start - just show error
        // Only move to Wait if process was already running
        if (currentProcessState?.state === "running" || data?.data?.isReadyForProcessing === "start") {
          setWaitingForReader(true);
          dispatch(setProcessState({
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            state: "wait",
          }));
          
          // Move to WAIT node immediately
          const normalize = (str: string) => str?.toLowerCase().replace(/[\s_-]/g, '') || '';
          const waitStepIndex = steps.findIndex((step: any) => {
            const stepName = step?.name || step;
            return normalize(stepName) === 'wait';
          });
          
          if (waitStepIndex !== -1) {
            setStepIndex(waitStepIndex);
            dispatch(setCurrentStep({
              processId: data?.data?.processId,
              deviceId: data?.data?.deviceId,
              step: waitStepIndex
            }));
            dispatch(setCurrentNode('Wait'));
            console.log('DeviceDetail: Moved to WAIT node due to device connection error');
          }
        }
        
        return; // Exit early - don't process other error handlers
      } 
      // Handle "process not running" errors - check if it's related to empty reference stock/inventory
      else if (status === 404 || msg?.toLowerCase?.().includes('not running') || msg?.toLowerCase?.().includes('process not found')) {
        console.log('DeviceDetail: Process not running error - checking if related to empty reference stock', {
          status,
          msg,
          processName: data?.data?.processName
        });
        
        // Check if this is Check Reference Stock and error might be due to empty inventory/reference stock
        const currentProcessName = data?.data?.processName || data?.data?.name;
        const isCheckReferenceStock = currentProcessName === ProcessName.CHECK_STOCK;
        
        // Check if error message indicates empty inventory or reference stock
        const isEmptyInventoryError = msg?.toLowerCase?.().includes('inventory') || 
                                     msg?.toLowerCase?.().includes('reference stock') ||
                                     msg?.toLowerCase?.().includes('empty') ||
                                     msg?.toLowerCase?.().includes('zero') ||
                                     msg?.toLowerCase?.().includes('no stock') ||
                                     msg?.toLowerCase?.().includes('no data');
        
        // For Check Reference Stock with 404, also check if it might be due to empty inventory
        // Backend might return 404 when inventory is empty
        const mightBeEmptyInventory = isCheckReferenceStock && (status === 404 || isEmptyInventoryError);
        
        let errorMessage = "Process is not running. Please try starting the process again.";
        
        // If it's Check Reference Stock and error suggests empty inventory, show user-friendly message and BLOCK
        if (mightBeEmptyInventory) {
          errorMessage = "Please set the reference stock before starting the process.";
          
          // CRITICAL: Block the process from starting - reset all state
          setIsStarting(false);
          isStartAttemptRef.current = false;
          
          // Ensure process state is reset
          dispatch(setProcessState({
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            state: "stopped",
          }));
          dispatchStopButton("start");
          
          dispatch(setProcessRunningModal({
            visible: true,
            processName: data?.data?.processName || "Check Reference Stock",
            deviceName: data?.data?.deviceName || "Device",
            errorMessage: errorMessage
          }));
          
          return; // CRITICAL: Exit early to prevent process from continuing
        }
        
        // For other "not running" errors, just show message but also reset state
        setIsStarting(false);
        isStartAttemptRef.current = false;
        
        dispatch(setProcessRunningModal({
          visible: true,
          processName: data?.data?.processName || "Process",
          deviceName: data?.data?.deviceName || "Device",
          errorMessage: errorMessage
        }));
      }
      else {
        // Show persistent modal for other errors with better error messages
        let errorMessage = msg || "An error occurred while starting the process. Please try again.";
        
        // Improve error messages for common scenarios
        if (status === 400) {
          errorMessage = `Cannot start the process. ${msg || "The request was invalid. Please check the device connection and try again."}`;
        } else if (status === 500) {
          errorMessage = `Server error occurred. ${msg || "Please try again later or contact support if the issue persists."}`;
        } else if (status === 503 || status === 502) {
          errorMessage = `Service unavailable. ${msg || "The server may be temporarily unavailable. Please try again later."}`;
        } else if (status === 504) {
          // Gateway Timeout - use the already processed user-friendly message
          errorMessage = msg || "The request timed out. The server may be temporarily unavailable. Please try again in a few moments.";
        } else if (!msg || msg === "Request failed with status code 400") {
          // Handle generic 400 errors with more helpful message
          errorMessage = `Cannot start the process. The device may be offline or disconnected. Please check the device connection and try again.`;
        }
        
        dispatch(setProcessRunningModal({
          visible: true,
          processName: data?.data?.processName || "Process",
          deviceName: data?.data?.deviceName || "Device",
          errorMessage: errorMessage
        }));
        
        setIsStarting(false);
        isStartAttemptRef.current = false;
      }
    } finally {
      // Reset starting state (error handlers may have already done this, but ensure it's reset)
      setIsStarting(false);
      // Note: isStartAttemptRef is managed by individual error handlers and success path
    }
  }

  // Monitor device status changes - auto-resume when device reconnects while at Wait node
  // Track previous device status to prevent unnecessary re-renders
  const prevDeviceStatusRef = useRef<string | undefined>();
  
  // Also send periodic notifications (every 1 minute) when device is offline
  useEffect(() => {
    const deviceStatus = mergedDeviceStatus[0]?.status;
    const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
    
    // Skip if device status hasn't changed to prevent unnecessary re-renders
    if (prevDeviceStatusRef.current === deviceStatus) {
      return;
    }
    prevDeviceStatusRef.current = deviceStatus;
    
    const currentStepName = steps[stepIndex]?.name || steps[stepIndex];
    const isAtWaitNode = currentStepName && (currentStepName.toLowerCase().includes('wait') || currentStepName === 'Wait' || currentNode?.toLowerCase() === 'wait');
    const currentTime = Date.now();
    const NOTIFICATION_INTERVAL_MS = 60 * 1000; // 1 minute in milliseconds
    
    // If we're at Wait node and device just came online, reset error flag and attempt to continue
    if (isAtWaitNode && isDeviceOnline && waitingForReader) {
      hasShownConnectionErrorRef.current = false; // Reset error flag when device comes back online
      lastDeviceOfflineNotificationRef.current = null; // Reset notification timer when device reconnects
      console.log('[DeviceDetail] Device came back online while at Wait node - attempting to continue process', {
        deviceStatus,
        currentStep: currentStepName,
        stepIndex,
        waitingForReader,
        isReadyForProcessing: data?.data?.isReadyForProcessing,
        processState: currentProcessState?.state
      });
      
      // Reset waiting state
      setWaitingForReader(false);
      
      // If process is in READY state, show success message
      if (data?.data?.isReadyForProcessing === "ready") {
        message.success({ 
          content: `Device "${data?.data?.deviceName || 'device'}" is now online. Process is ready to continue.`,
          key: "device-reconnected",
          duration: 3 
        });
        console.log('[DeviceDetail] Device reconnected - process is ready, user can press Start to continue');
      } else if (currentProcessState?.state === "wait" || currentProcessState?.state === "running") {
        // Process was running but waiting - backend should auto-resume
        message.info({ 
          content: `Device "${data?.data?.deviceName || 'device'}" is now online. Process will continue automatically.`,
          key: "device-reconnected-auto",
          duration: 3 
        });
        console.log('[DeviceDetail] Device reconnected - process should auto-resume via backend');
      }
    }
    
    // If device is offline and we're at Wait node, check if we should send periodic notification
    // ONLY if this is Check Reference Stock workflow started via Start button
    // Skip for Edit Reference Stock (startedViaEditReferenceStockButton) and Apply button (startedViaApplyButton) workflows
    if (isAtWaitNode && !isDeviceOnline && waitingForReader) {
      const currentProcessName = data?.data?.processName || data?.data?.name;
      const isCheckReferenceStock = currentProcessName === ProcessName.CHECK_STOCK;
      
      // Check active process for flags to determine how the workflow was started
      // Backend stores startedViaStartButton, startedViaEditReferenceStockButton, and startedViaApplyButton flags in state
      const activeProcesses = validateActiveProcess?.data?.result || [];
      const currentProcess = activeProcesses.find(
        (p: any) => p.processId === data?.data?.processId && p.deviceId === data?.data?.deviceId
      );
      
      // Extract flags from active process state (backend stores these in the state document)
      const startedViaStartButton = currentProcess?.startedViaStartButton === true;
      const startedViaApplyButton = currentProcess?.startedViaApplyButton === true;
      const startedViaEditReferenceStockButton = currentProcess?.startedViaEditReferenceStockButton === true;
      
      console.log('[DeviceDetail] Checking how process was started:', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        startedViaStartButton,
        startedViaApplyButton,
        startedViaEditReferenceStockButton,
        currentProcessName: currentProcessName,
        isCheckReferenceStock,
        note: 'Only processes started via Start button should send notifications and increment cycles'
      });
      
      // Only send periodic notifications for Check Reference Stock workflow started via Start button
      // Skip notifications for Edit Reference Stock and Apply button workflows
      // Check: startedViaStartButton must be true AND (startedViaApplyButton and startedViaEditReferenceStockButton must be false/undefined)
      const shouldSendNotifications = isCheckReferenceStock && 
        startedViaStartButton === true && 
        startedViaApplyButton !== true && 
        startedViaEditReferenceStockButton !== true;
      
      if (shouldSendNotifications) {
        // CRITICAL: Check if process is still running before sending notification
        // Backend checks process state before sending notifications - frontend should do the same
        const processState = currentProcessState?.state;
        const isReadyForProcessing = data?.data?.isReadyForProcessing;
        
        // Check if process is stopped/completed/deleted
        const isProcessStopped = 
          processState === "stop" || 
          processState === "STOP" || 
          processState === "COMPLETED" || 
          processState === "completed" || 
          processState === "exit" ||
          isReadyForProcessing === "stop" ||
          isReadyForProcessing === "STOP" ||
          isReadyForProcessing === "COMPLETED" ||
          isReadyForProcessing === "completed" ||
          isReadyForProcessing === "exit";
        
        const isProcessInActiveList = !!currentProcess;
        
        // Only send notification if process is still running and active
        if (isProcessStopped || !isProcessInActiveList) {
          console.log('[DeviceDetail] Skipping periodic notification - process is stopped or not in active list', {
            processState,
            isReadyForProcessing,
            isProcessStopped,
            isProcessInActiveList,
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            note: 'Process has been stopped/completed or state has been deleted. Backend also checks this before sending notifications.'
          });
          // Reset waiting state and notification timer if process is stopped
          if (isProcessStopped || !isProcessInActiveList) {
            setWaitingForReader(false);
            lastDeviceOfflineNotificationRef.current = null;
          }
          return; // Exit early - don't send notification
        }
        
        const lastNotificationTime = lastDeviceOfflineNotificationRef.current;
        const timeSinceLastNotification = lastNotificationTime ? currentTime - lastNotificationTime : Infinity;
        
        // Send notification if:
        // 1. No previous notification has been sent (first time), OR
        // 2. At least 1 minute has passed since the last notification
        // 3. Process is still running (checked above)
        if (!lastNotificationTime || timeSinceLastNotification >= NOTIFICATION_INTERVAL_MS) {
          console.log('[DeviceDetail] Device is offline - sending periodic notification', {
            deviceStatus,
            lastNotificationTime: lastNotificationTime ? new Date(lastNotificationTime).toISOString() : 'never',
            timeSinceLastNotification: timeSinceLastNotification / 1000 + ' seconds',
            isAtWaitNode,
            waitingForReader,
            currentProcessName,
            isCheckReferenceStock,
            startedViaStartButton,
            startedViaApplyButton,
            startedViaEditReferenceStockButton,
            processState,
            isReadyForProcessing,
            isProcessInActiveList,
            note: 'Process is still running and started via Start button - notification will be sent. Backend will also verify process state and flags before sending email/webhook.'
          });
          
          // Update last notification time
          lastDeviceOfflineNotificationRef.current = currentTime;
          
          // Emit WebSocket event to trigger backend notification
          // The backend should listen for this event and send email/webhook notifications
          // Backend will also check process state before sending (as per backend changes)
          const notificationEventName = `${WebsocketKey.Notification_error}-${data?.data?.deviceId}-${data?.data?.processId}`;
          const notificationPayload = {
            deviceId: data?.data?.deviceId,
            processId: data?.data?.processId,
            userId: user?.userId,
            deviceName: data?.data?.deviceName || 'Device',
            processName: data?.data?.processName || 'Check Reference Stock',
            error: {
              message: `Process has been started but device is disconnected or offline. Please check the reader and restart the reader. Process will continue once device is reconnected.`,
              type: 'DEVICE_OFFLINE',
              timestamp: new Date().toISOString()
            },
            data: {
              message: `Process has been started but device is disconnected or offline. Please check the reader and restart the reader. Process will continue once device is reconnected.`,
              deviceStatus,
              isPeriodicNotification: true
            }
          };
          
          socket.emit(notificationEventName, notificationPayload);
          console.log('[DeviceDetail] Emitted device offline notification event', {
            eventName: notificationEventName,
            payload: notificationPayload
          });
          
          // Also show a frontend message
          message.warning({ 
            content: `Device "${data?.data?.deviceName || 'device'}" is still offline. Notification sent.`,
            key: "device-offline-periodic",
            duration: 5 
          });
        }
      } else {
        // Skip notifications if:
        // 1. Not Check Reference Stock workflow, OR
        // 2. Started via Apply button, OR
        // 3. Started via Edit Reference Stock button, OR
        // 4. Not started via Start button
        console.log('[DeviceDetail] Skipping periodic notification - process not started via Start button', {
          currentProcessName,
          isCheckReferenceStock,
          startedViaStartButton,
          startedViaApplyButton,
          startedViaEditReferenceStockButton,
          shouldSendNotifications: false,
          note: 'Notifications are skipped for Edit Reference Stock (startedViaEditReferenceStockButton) and Apply button (startedViaApplyButton) workflows. Only processes started via Start button (startedViaStartButton=true) should send notifications and increment cycles.'
        });
      }
    }
    
    // If device goes offline while at Wait, ensure we stay at Wait
    if (isAtWaitNode && !isDeviceOnline) {
      const waitStepIndex = steps.findIndex((step: any) => {
        const stepName = step?.name || step;
        return stepName?.toLowerCase().replace(/[\s_-]/g, '') === 'wait';
      });
      
      if (waitStepIndex !== -1 && stepIndex !== waitStepIndex) {
        console.log('[DeviceDetail] Device went offline - forcing step back to Wait');
        setStepIndex(waitStepIndex);
        dispatch(setCurrentStep({
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          step: waitStepIndex
        }));
        setWaitingForReader(true);
      }
    }
  }, [mergedDeviceStatus, stepIndex, steps, dispatch, data?.data?.processId, data?.data?.deviceId, currentNode, waitingForReader, data?.data?.isReadyForProcessing, data?.data?.deviceName, data?.data?.processName, currentProcessState?.state, user?.userId, socket, validateActiveProcess?.data?.result]);

  // activity logs button - using new createAndStart process mutation

  // Add error handling for new mutation - use ProcessRunningModal for all errors
  useEffect(() => {
    if (startInfo?.isError) {
      const errorMessage = startInfo?.error?.data?.error?.message;
      
      console.log('DeviceDetail: startProcess error occurred', {
        errorMessage,
        processName: data?.data?.processName,
        deviceName: data?.data?.deviceName,
        deviceId: data?.data?.deviceId,
        processId: data?.data?.processId,
        timestamp: new Date().toISOString()
      });

      // Show ProcessRunningModal for all errors
      dispatch(setProcessRunningModal({
        visible: true,
        processName: data?.data?.processName || "Process",
        deviceName: data?.data?.deviceName || "Device",
        errorMessage: errorMessage || "An error occurred while starting the process"
      }));
    }
  }, [startInfo?.isError, dispatch, data?.data?.processName, data?.data?.deviceName, data?.data?.deviceId, data?.data?.processId, startInfo?.error])

  // Add success handling for new mutation
  useEffect(() => {
    if (startInfo?.isSuccess) {
      console.log('DeviceDetail: startProcess success', {
        processName: data?.data?.processName,
        deviceId: data?.data?.deviceId,
        processId: data?.data?.processId,
        timestamp: new Date().toISOString()
      });
      
      // CRITICAL: For Check Reference Stock, verify reference stock exists even after "success"
      // This is a safeguard in case backend allows process to start without validation
      const currentProcessName = data?.data?.processName || data?.data?.name;
      if (currentProcessName === ProcessName.CHECK_STOCK) {
        // Verify reference stock exists as a safeguard
        getReferenceStock({
          processId: data?.data?.processId,
          siteId: data?.data?.siteId,
          zoneId: data?.data?.zoneId
        }).unwrap().then((referenceStockResult: any) => {
          const tags = referenceStockResult?.result?.tags || referenceStockResult?.result?.data || [];
          const hasTags = Array.isArray(tags) && tags.length > 0;
          const hasData = referenceStockResult?.result && 
                         typeof referenceStockResult.result === 'object' && 
                         Object.keys(referenceStockResult.result).length > 0 &&
                         !Array.isArray(referenceStockResult.result);
          const hasReferenceStock = hasTags || (hasData && !Array.isArray(referenceStockResult.result));
          
          if (!hasReferenceStock) {
            const activeProcesses = validateActiveProcess?.data?.result || [];
            const hasOtherActiveProcessWithSameProcessId = activeProcesses.some((p: any) => {
              if (p.processId !== data?.data?.processId) {
                return false;
              }
              const differentDevice = p.deviceId !== data?.data?.deviceId;
              const differentSite = (p.siteId || '') !== (data?.data?.siteId || '');
              const differentZone = (p.zoneId || '') !== (data?.data?.zoneId || '');
              return differentDevice || differentSite || differentZone;
            });

            // Reference stock is empty - stop the process immediately
            console.error('DeviceDetail: Process started but reference stock is empty - stopping process', {
              processId: data?.data?.processId,
              deviceId: data?.data?.deviceId,
              siteId: data?.data?.siteId,
              zoneId: data?.data?.zoneId,
              hasOtherActiveProcessWithSameProcessId
            });

            if (!hasOtherActiveProcessWithSameProcessId) {
              // Stop only when this processId is not shared across other sites/zones/devices
              stopProcess({
                userId: user?.userId,
                processId: data?.data?.processId,
                deviceId: data?.data?.deviceId,
                siteId: data?.data?.siteId,
                zoneId: data?.data?.zoneId,
                type: "stop"
              });
            } else {
              console.warn('DeviceDetail: Skipping auto-stop to avoid affecting other sites/zones using same processId', {
                processId: data?.data?.processId,
                deviceId: data?.data?.deviceId,
                siteId: data?.data?.siteId,
                zoneId: data?.data?.zoneId
              });
            }
            
            // Reset state
            dispatch(setProcessState({
              processId: data?.data?.processId,
              deviceId: data?.data?.deviceId,
              state: "stopped",
            }));
            dispatchStopButton("start");
            isStartAttemptRef.current = false;
            
            // Show error
            const errorMessage = "Please set the reference stock before starting the process.";
            message.error({
              content: errorMessage,
              key: "reference-stock-empty-after-start",
              duration: 5
            });
            
            dispatch(setProcessRunningModal({
              visible: true,
              processName: data?.data?.processName || "Check Reference Stock",
              deviceName: data?.data?.deviceName || "Device",
              errorMessage: errorMessage
            }));
            
            return; // Don't mark as successfully started
          }
          
          // Reference stock exists - proceed normally
          dispatchStopButton("pause");
          message.success({ content: "Process started", key: "notificationKey", className: "successMessage", duration: 2 });
          isStartAttemptRef.current = false;
        }).catch((refStockError: any) => {
          // If we can't verify reference stock, log but don't block (might be network issue)
          console.warn('DeviceDetail: Could not verify reference stock after process start', {
            error: refStockError
          });
          // Proceed with normal success flow
          dispatchStopButton("pause");
          message.success({ content: "Process started", key: "notificationKey", className: "successMessage", duration: 2 });
          isStartAttemptRef.current = false;
        });
      } else {
        // Not Check Reference Stock - proceed normally
        dispatchStopButton("pause");
        message.success({ content: "Process started", key: "notificationKey", className: "successMessage", duration: 2 });
      isStartAttemptRef.current = false; // Reset start attempt flag on success
    }
    }
  }, [startInfo?.isSuccess, data?.data?.processName, data?.data?.deviceId, data?.data?.processId, data?.data?.siteId, data?.data?.zoneId, dispatch, getReferenceStock, stopProcess, user?.userId])
  // Removed old startProcess reference

  const onStopClick = () => {
    // 
    stopProcess({
      stopBy: user?.userId,
      processId: data?.data?.processId,
      processName: data?.data?.processName,
      deviceId: data?.data?.deviceId,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      type: "exit",
      activity_logs: true,
      userId: user?.userId,
    });
  };

  // useEffect(() => {
  //   if (!setIsPaused) {
  //     message.success("Process has been paused successfully");
  //   }
  // }, [setIsPaused]);

  const onPauseConfirmClick = async () => {
    // Close modal immediately
    setPauseModal(false);

    const pauseStateId = resolveStateIdForApi();
    if (!pauseStateId) {
      message.error("stateId is required to pause process. Please restart the process.");
      return;
    }
    
    // Set state to pause-processing - backend will finish current cycle then pause
    dispatch(setProcessState({
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      state: "pause-processing",
    }));
    
    // Update button to show PAUSING state immediately
    pausingUiForProcessKeyRef.current = `${data?.data?.processId} ${data?.data?.deviceId}`;
    console.log('[DeviceDetail] Pause confirmed - marking this run as pause owner and setting global button to PAUSING', {
      ownerKey: pausingUiForProcessKeyRef.current,
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
    });
    dispatchStopButton("pausing");
    
    try {
      // Send pause request - backend will finish current cycle and then pause
      // Backend has fallback: if workflow not in runningWorkflows, it queries state directly
      // This makes pause more resilient to timing issues and state mismatches
      await pauseProcess({
      userId: user?.userId,
      stateId: pauseStateId,
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      type: "pause",
      }).unwrap();
      
      // Success message will be shown when state transitions from pause-processing to pause
      // No message here to avoid duplicates
    } catch (err: any) {
      const msg = err?.data?.error?.message || err?.data?.message || err?.message || String(err);
      console.error('Pause process error:', { msg, errorData: err?.data });
      message.error(msg || "Failed to pause process");
      
      pausingUiForProcessKeyRef.current = null;
      console.log('[DeviceDetail] Pause failed - clearing pause owner and restoring RUNNING/PAUSE button', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
      });
      // Reset state if pause failed - go back to running state
      dispatch(setProcessState({
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        state: "running",
      }));
      
      // Reset button back to "pause" if pause failed (process is still running)
      dispatchStopButton("pause");
    }
  };

  const onPauseClick = () => {
    // Clear resume attempt flag since we're now pausing
    isResumeAttemptRef.current = false;
    setPauseModal(true);
  }

  const onResumeClick = () => {
    console.log('[Device Detail] Resume button clicked - initiating resume process', {
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      processName: data?.data?.processName,
      currentButtonState: stopbutton,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      userId: user?.userId,
      timestamp: new Date().toISOString(),
      note: 'Resume API call initiated - button should change from "resume/exit" to "pause" on success'
    });
    
    // Reset toast flag for new resume attempt
    hasShownResumeToastRef.current = false;
    
    const resumeStateId = resolveStateIdForApi();
    if (!resumeStateId) {
      message.error("stateId is required to resume process. Please restart the process.");
      return;
    }

    resumeProcess({
      userId: user?.userId,
      stateId: resumeStateId,
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      type: "pause",
      workflowName: data?.data?.workflowName,
    });
  };



  // Handle process start success
  useEffect(() => {
    if (startInfo?.isSuccess) {
      dispatch(setProcessState({
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        state: "running",
      }));
    }
  }, [startInfo?.isSuccess, data?.data?.processId, data?.data?.deviceId, dispatch]);

  // Handle process pause API response
  useEffect(() => {
    if (pauseProcessInfo?.isError) {
      const errorMsg = pauseProcessInfo?.error?.data?.error?.message || pauseProcessInfo?.error?.data?.message || pauseProcessInfo?.error?.message;
      console.error("Pause process error:", errorMsg);
      message.error(errorMsg || "Failed to pause process");
      
      // Reset state if pause failed - go back to running
      dispatch(setProcessState({
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        state: "running",
      }));
      // Reset button back to "pause" if pause failed (process is still running)
      pausingUiForProcessKeyRef.current = null;
      dispatchStopButton("pause");
    }
    // Success message is shown when state transitions from pause-processing to pause (below)
  }, [pauseProcessInfo?.isError, data?.data?.processId, data?.data?.deviceId, dispatch]);
  
  // Track previous state to detect transitions (using ref to avoid stale closures)
  const prevProcessStateRef = useRef<string | undefined>();
  const hasShownPauseSuccessRef = useRef<boolean>(false);
  const isStartAttemptRef = useRef<boolean>(false);
  const isResumeAttemptRef = useRef<boolean>(false); // Track when resume API just succeeded
  const hasHandledResumeSuccessRef = useRef<boolean>(false); // Track if resume success has been handled to prevent duplicates
  const hasShownResumeToastRef = useRef<boolean>(false); // Track if resume success toast has been shown to prevent duplicates
  const hasShownConnectionErrorRef = useRef<boolean>(false);
  const lastDeviceOfflineNotificationRef = useRef<number | null>(null); // Track last notification time for device offline
  // Track when we entered Notify node to ensure minimum stay duration for error notifications
  const notifyEnterTimeRef = useRef<number | null>(null);
  const pendingStateTransitionRef = useRef<{ stateName: string; sourceProcessId?: string; sourceDeviceId?: string } | null>(null);
  const notifyTransitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track time of last workflow state message so we don't clean up while the process is clearly active
  const lastWorkflowStateTimeRef = useRef<number | null>(null);
  const currentProcessStateRef = useRef<any>({}); // Track current process state for WebSocket handlers
  // Track missing items count to determine if we should go to Notify node
  const missingItemsCountRef = useRef<number>(0);
  
  // Handle state transition from pause-processing to pause (when backend actually pauses)
  useEffect(() => {
    const prevState = normalizeState(prevProcessStateRef.current);
    const currentState = normalizeState(currentProcessState?.state);
    
    // Show transition toast when entering pause-processing state
    if (prevState !== "pause-processing" && currentState === "pause-processing") {
      message.info({
        content: "Process will be paused after completing the current node",
        key: "pauseProcessing",
        duration: 5,
      });
    }
    
    // Show success message and update button state when transitioning from pause-processing to pause
    if (prevState === "pause-processing" && currentState === "pause" && !hasShownPauseSuccessRef.current) {
      // Update button from "pausing" to "resume" when pause completes
      pausingUiForProcessKeyRef.current = null;
      dispatchStopButton("resume");
      
      // Show success message notification only (no banner)
      message.success({
        content: "Process paused successfully", 
        key: "pauseSuccess", 
        className: "successMessage",
        duration: 3 
      });
      hasShownPauseSuccessRef.current = true;
    }
    
    // Reset success message flag when state changes away from pause
    if (currentState !== "pause" && currentState !== "pause-processing") {
      hasShownPauseSuccessRef.current = false;
    }
    
    // Update ref for next comparison (store original state value)
    prevProcessStateRef.current = currentProcessState?.state;
  }, [currentProcessState?.state, dispatch]);

  // Also check backend state directly to catch pause completion (backup detection)
  useEffect(() => {
    if (validateActiveProcess?.isSuccess && processId && deviceId) {
      const activeProcesses = validateActiveProcess?.data?.result || [];
      const currentProcess = activeProcesses.find(
        (p: any) => p.processId === processId && p.deviceId === deviceId
      );
      
      if (currentProcess && currentProcess.isReadyForProcessing) {
        const backendState = normalizeState(currentProcess.isReadyForProcessing);
        const normalizedCurrentState = normalizeState(currentProcessState?.state);
        
        // If backend is "pause" and we're still in "pause-processing" or button is "pausing", update
        if (backendState === "pause" && 
            (normalizedCurrentState === "pause-processing" || stopbutton === "pausing")) {
          console.log('[Device Detail] Backend pause detected - updating state and button', {
            processId,
            deviceId,
            backendState,
            currentState: normalizedCurrentState,
            currentStopbutton: stopbutton
          });
          
          // Update Redux state to match backend
          dispatch(setProcessState({
            processId,
            deviceId,
            state: "pause",
          }));
          
          // Update button to "resume" when pause completes
          // Note: Banner will be shown by the first useEffect when Redux state updates to "pause"
          if (stopbutton === "pausing") {
            pausingUiForProcessKeyRef.current = null;
            dispatchStopButton("resume");
            // Don't show banner here - let the first useEffect handle it when state updates
          }
        }
      }
    }
  }, [validateActiveProcess?.isSuccess, validateActiveProcess?.data?.result, processId, deviceId, currentProcessState?.state, stopbutton, dispatch, dispatchStopButton]);

  // Handle process resume success
  useEffect(() => {
    if (resumeProcessInfo?.isSuccess && !hasHandledResumeSuccessRef.current) {
      // Mark as handled immediately to prevent duplicate executions
      hasHandledResumeSuccessRef.current = true;
      
      console.log('[Device Detail] Resume process success - updating button state from resume/exit to pause', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        processName: data?.data?.processName,
        currentButtonState: stopbutton,
        previousButtonState: stopbutton,
        newButtonState: 'pause',
        timestamp: new Date().toISOString(),
        note: 'Button should change from "resume/exit" to "pause" when process resumes successfully'
      });
      
      // Set resume attempt flag to prevent backend state check from overriding button
      isResumeAttemptRef.current = true;
      
      dispatch(setProcessState({
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        state: "running",
      }));
      
      // Update button from "resume" to "pause" when process resumes
      console.log('[Device Detail] Dispatching setStopButton("pause") - changing button from resume to pause', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        fromButtonState: stopbutton,
        toButtonState: 'pause',
        timestamp: new Date().toISOString()
      });
      pausingUiForProcessKeyRef.current = null;
      dispatchStopButton("pause");
      
      console.log('[Device Detail] Resume button state change completed - button should now show PAUSE', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        expectedButtonState: 'pause',
        timestamp: new Date().toISOString()
      });
      
      // Don't show toast here - it will be shown when button actually changes (in useEffect below)
      
      // Clear resume attempt flag after 8 seconds (give backend time to update)
      // This prevents the flag from staying true forever if backend never updates
      setTimeout(() => {
        if (isResumeAttemptRef.current) {
          console.log('[Device Detail] Clearing resume attempt flag after timeout (8 seconds)', {
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId
          });
          isResumeAttemptRef.current = false;
        }
      }, 8000);
    }
    
    // Reset the flags when resumeProcessInfo changes (new attempt or loading state)
    if (resumeProcessInfo?.isLoading || (!resumeProcessInfo?.isSuccess && !resumeProcessInfo?.isError)) {
      hasHandledResumeSuccessRef.current = false;
      hasShownResumeToastRef.current = false; // Reset toast flag for new resume attempt
    }
  }, [resumeProcessInfo?.isSuccess, resumeProcessInfo?.isLoading, data?.data?.processId, data?.data?.deviceId, data?.data?.processName, stopbutton, dispatch]);

  // Show resume success toast only when button actually changes from "resume" to "pause"
  useEffect(() => {
    const prevButton = prevStopButtonRef.current;
    const currentButton = stopbutton;
    
    // Detect transition from "resume" to "pause" (resume completed)
    // Only show toast once per resume operation
    if (prevButton === "resume" && currentButton === "pause" && resumeProcessInfo?.isSuccess && !hasShownResumeToastRef.current) {
      // Mark as shown immediately to prevent duplicate toasts
      hasShownResumeToastRef.current = true;
      
      console.log('[Device Detail] Button changed from resume to pause - showing success toast', {
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        prevButton,
        currentButton,
        timestamp: new Date().toISOString()
      });
      
      // Show success message notification when button actually updates
      message.success({
        content: "Process Resumed Successfully",
        key: "resumeSuccess",
        className: "successMessage",
        duration: 2,
      });
    }
    
    // Update ref for next comparison
    prevStopButtonRef.current = stopbutton;
  }, [stopbutton, resumeProcessInfo?.isSuccess, data?.data?.processId, data?.data?.deviceId]);

  // Unified state update function for node transitions
  const updateNodeState = (stateName: string, sourceProcessId?: string, sourceDeviceId?: string) => {
    if (!stateName) return;

        const normalize = (str: string) => str?.toLowerCase().replace(/[\s_-]/g, '') || '';
        const normalizedStateName = normalize(stateName);
        const normalizedCurrentNode = normalize(currentNode || '');
        
        // CRITICAL: Skip Notify node for Edit Reference Stock and other workflows
        // Only allow Notify node when Check Reference Stock workflow is running
        const currentProcessName = processName || data?.data?.processName || data?.data?.name;
        const isCheckReferenceStock = currentProcessName === ProcessName.CHECK_STOCK;
        
        // CRITICAL: Only go to Notify node when there are missing items from Scan Multiple
        if (normalizedStateName === 'notify' && isCheckReferenceStock) {
          const missingItemsCount = missingItemsCountRef.current || 0;
          
          if (missingItemsCount === 0) {
            console.log('[Check Reference Stock] Skipping Notify node - no missing items from Scan Multiple', {
              processName: currentProcessName,
              stateName: stateName,
              missingItemsCount: missingItemsCount,
              note: 'Notify node should only be shown when there are missing items. Skipping to Wait node instead.'
            });
            
            // Find Wait node index to transition to instead
            const waitStepIndex = steps.findIndex((step: any) => {
              const stepName = step?.name || step;
              return normalize(stepName) === 'wait';
            });
            
            if (waitStepIndex !== -1) {
              // Transition directly to Wait instead of Notify
              console.log('[Check Reference Stock] Transitioning to Wait node instead of Notify (no missing items)');
              const targetProcessId = sourceProcessId || processId;
              const targetDeviceId = sourceDeviceId || deviceId;
              
              setStepIndex(waitStepIndex);
              dispatch(setCurrentStep({
                processId: targetProcessId,
                deviceId: targetDeviceId,
                step: waitStepIndex
              }));
              dispatch(setCurrentNode('Wait'));
              
              // Check device status for Wait node
              const deviceStatus = mergedDeviceStatus[0]?.status;
              const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
              if (!isDeviceOnline) {
                console.log('Device is offline - staying at Wait node until device comes online');
              }
              return; // Skip the rest of the function
            } else {
              console.warn('[Check Reference Stock] Wait node not found in steps - cannot skip Notify');
              // If Wait node not found, just skip this transition entirely
              return;
            }
          } else {
            console.log('[Check Reference Stock] Proceeding to Notify node - missing items detected', {
              missingItemsCount: missingItemsCount
            });
          }
        }
        
        if (normalizedStateName === 'notify' && !isCheckReferenceStock) {
          console.log('[Device Detail] Skipping Notify node - not Check Reference Stock workflow', {
            processName: currentProcessName,
            stateName: stateName,
            note: 'Notify node is only for Check Reference Stock workflow. Skipping to Wait node instead.'
          });
          
          // Find Wait node index to transition to instead
          const waitStepIndex = steps.findIndex((step: any) => {
            const stepName = step?.name || step;
            return normalize(stepName) === 'wait';
          });
          
          if (waitStepIndex !== -1) {
            // Transition directly to Wait instead of Notify
            console.log('[Device Detail] Transitioning to Wait node instead of Notify');
            const targetProcessId = sourceProcessId || processId;
            const targetDeviceId = sourceDeviceId || deviceId;
            
            setStepIndex(waitStepIndex);
            dispatch(setCurrentStep({
              processId: targetProcessId,
              deviceId: targetDeviceId,
              step: waitStepIndex
            }));
            dispatch(setCurrentNode('Wait'));
            
            // Check device status for Wait node
            const deviceStatus = mergedDeviceStatus[0]?.status;
            const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
            if (!isDeviceOnline) {
              console.log('Device is offline - staying at Wait node until device comes online');
            }
            return; // Skip the rest of the function
          } else {
            console.warn('[Device Detail] Wait node not found in steps - cannot skip Notify');
            // If Wait node not found, just skip this transition entirely
            return;
          }
        }
        
        // CRITICAL: If we're currently at Notify but this is NOT Check Reference Stock workflow,
        // transition away immediately (don't wait for minimum duration)
        if (normalizedCurrentNode === 'notify' && normalizedStateName !== 'notify' && !isCheckReferenceStock) {
          console.log('[Device Detail] Currently at Notify node but not Check Reference Stock workflow - transitioning away immediately', {
            processName: currentProcessName,
            fromState: 'Notify',
            toState: stateName
          });
          // Clear any pending Notify transitions since this is not Check Reference Stock
          if (notifyTransitionTimeoutRef.current) {
            clearTimeout(notifyTransitionTimeoutRef.current);
            notifyTransitionTimeoutRef.current = null;
          }
          notifyEnterTimeRef.current = null;
          // Continue with normal transition (don't return here)
        }
        
        // CRITICAL: If we're currently at Notify and trying to transition to Wait (or any other state),
        // ensure we've stayed at Notify for at least 3-4 seconds to allow notification to be sent
        // This only applies to Check Reference Stock workflow
        if (normalizedCurrentNode === 'notify' && normalizedStateName !== 'notify' && isCheckReferenceStock) {
          const timeAtNotify = notifyEnterTimeRef.current;
          if (timeAtNotify !== null) {
            const timeSpentAtNotify = Date.now() - timeAtNotify;
            const MIN_NOTIFY_DURATION = 3500; // 3.5 seconds minimum
            
            if (timeSpentAtNotify < MIN_NOTIFY_DURATION) {
              const remainingTime = MIN_NOTIFY_DURATION - timeSpentAtNotify;
              console.log(`[Check Reference Stock] Transition from Notify to ${stateName} delayed - must stay at Notify for at least ${MIN_NOTIFY_DURATION}ms (${remainingTime}ms remaining)`);
              
              // Store the pending transition
              pendingStateTransitionRef.current = { stateName, sourceProcessId, sourceDeviceId };
              
              // Clear any existing timeout
              if (notifyTransitionTimeoutRef.current) {
                clearTimeout(notifyTransitionTimeoutRef.current);
              }
              
              // Schedule the transition after remaining time
              notifyTransitionTimeoutRef.current = setTimeout(() => {
                if (pendingStateTransitionRef.current) {
                  console.log(`[Check Reference Stock] Minimum Notify duration elapsed - proceeding with transition to ${pendingStateTransitionRef.current.stateName}`);
                  const pending = pendingStateTransitionRef.current;
                  pendingStateTransitionRef.current = null;
                  notifyEnterTimeRef.current = null;
                  // Recursively call updateNodeState with the pending transition
                  updateNodeState(pending.stateName, pending.sourceProcessId, pending.sourceDeviceId);
                }
              }, remainingTime);
              
              return; // Don't proceed with transition yet
            } else {
              // Minimum duration has passed, clear the tracking
              notifyEnterTimeRef.current = null;
              if (notifyTransitionTimeoutRef.current) {
                clearTimeout(notifyTransitionTimeoutRef.current);
                notifyTransitionTimeoutRef.current = null;
              }
            }
          }
        }
        
        // Reset missing items count when entering Scan Multiple node (new cycle)
        if (normalizedStateName === 'scanmultiple' && normalizedCurrentNode !== 'scanmultiple' && isCheckReferenceStock) {
          missingItemsCountRef.current = 0;
          console.log('[Check Reference Stock] Entered Scan Multiple node - resetting missing items count for new cycle');
        }
        
        // Track when we enter Notify node (before updating Redux state)
        // Only track for Check Reference Stock workflow
        if (normalizedStateName === 'notify' && normalizedCurrentNode !== 'notify' && isCheckReferenceStock) {
          notifyEnterTimeRef.current = Date.now();
          console.log('[Check Reference Stock] Entered Notify node - will stay for minimum 3.5 seconds to ensure notification is sent', {
            missingItemsCount: missingItemsCountRef.current
          });
          // Clear any pending transitions when entering Notify
          if (pendingStateTransitionRef.current) {
            pendingStateTransitionRef.current = null;
          }
          if (notifyTransitionTimeoutRef.current) {
            clearTimeout(notifyTransitionTimeoutRef.current);
            notifyTransitionTimeoutRef.current = null;
          }
        }
        
        const matchingIndex = steps.findIndex((step: any) => {
          const stepName = step?.name || step;
      return normalize(stepName) === normalizedStateName;
        });
        
        if (matchingIndex !== -1) {
      const targetProcessId = sourceProcessId || processId;
      const targetDeviceId = sourceDeviceId || deviceId;
      
          setStepIndex(matchingIndex);
          dispatch(setCurrentStep({
        processId: targetProcessId,
        deviceId: targetDeviceId,
            step: matchingIndex
          }));
      // Update currentNode in Redux - this happens after we've checked the previous state
      dispatch(setCurrentNode(stateName));
          
          // Special handling for Wait node - check device status
          if (normalizedStateName === 'wait') {
            const deviceStatus = mergedDeviceStatus[0]?.status;
            const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
            if (!isDeviceOnline) {
          console.log('Device is offline - staying at Wait node until device comes online');
            }
          }
        } else {
          console.warn(`No matching step found for state: ${stateName}. Available steps:`, steps.map((s: any) => s?.name || s));
        }
  };

  // WebSocket listeners for workflow state changes
  useEffect(() => {
    if (!deviceId || !processId) return;

    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const siteId = data?.data?.siteId || '';
    const zoneId = data?.data?.zoneId || '';

    // Listen for general state changes (namespaced event)
    const stateChangeEventName = `${WebsocketKey.STATE_CHANGE}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleStateChange = (wsData: any) => {
      console.log('[Check Reference Stock] StateChange WebSocket message received:', {
        eventName: stateChangeEventName,
        wsData: wsData,
        stateName: wsData?.data?.stateName,
        receivedProcessId: wsData?.data?.processId,
        receivedDeviceId: wsData?.data?.deviceId,
        expectedProcessId: processId,
        expectedDeviceId: deviceId
      });
      
      if (wsData?.data?.stateName) {
        // Verify siteId, zoneId, processId and deviceId match (if provided in data)
        const wsSiteId = wsData?.data?.siteId || '';
        const wsZoneId = wsData?.data?.zoneId || '';
        if (
          (!wsData?.data?.processId || wsData?.data?.processId === processId) &&
          (!wsData?.data?.deviceId || wsData?.data?.deviceId === deviceId) &&
          (!wsSiteId || wsSiteId === siteId) &&
          (!wsZoneId || wsZoneId === zoneId)
        ) {
          console.log('[Check Reference Stock] Updating node state:', wsData.data.stateName);
          
          // Update cycle counts in real-time from WebSocket event if available
          const processObject = wsData?.processObject || wsData?.data?.processObject || wsData?.data?.data?.processObject;
          const totalCycles = processObject?.totalCycles ?? wsData?.totalCycles ?? wsData?.data?.totalCycles ?? wsData?.data?.data?.totalCycles;
          const totalSuccess = processObject?.totalSuccess ?? wsData?.totalSuccess ?? wsData?.data?.totalSuccess ?? wsData?.data?.data?.totalSuccess;
          const totalFailed = processObject?.totalFailed ?? wsData?.totalFailed ?? wsData?.data?.totalFailed ?? wsData?.data?.data?.totalFailed;
          
          if (totalCycles !== undefined || totalSuccess !== undefined || totalFailed !== undefined) {
            setSyncedData((prevSyncedData: any) => {
              const keepCycles = shouldKeepPreviousTotalCycles(prevSyncedData, totalCycles);
              const updated = {
                ...prevSyncedData,
                ...(totalCycles !== undefined && !keepCycles && { totalCycles }),
                ...(totalSuccess !== undefined && { totalSuccess }),
                ...(totalFailed !== undefined && { totalFailed })
              };
              
              if (prevSyncedData?.totalCycles !== totalCycles || 
                  prevSyncedData?.totalSuccess !== totalSuccess || 
                  prevSyncedData?.totalFailed !== totalFailed) {
                console.log('[Check Reference Stock] Updated cycle counts from state-change event:', {
                  previous: {
                    totalCycles: prevSyncedData?.totalCycles,
                    totalSuccess: prevSyncedData?.totalSuccess,
                    totalFailed: prevSyncedData?.totalFailed
                  },
                  updated: {
                    totalCycles,
                    totalSuccess,
                    totalFailed
                  },
                  stateName: wsData.data.stateName,
                  timestamp: new Date().toISOString()
                });
              }
              
              return updated;
            });
          }
          
          // Update timestamp of last workflow state message for cleanup logic
          lastWorkflowStateTimeRef.current = Date.now();
          
          updateNodeState(
            wsData.data.stateName,
            wsData.data.processId,
            wsData.data.deviceId
          );
        } else {
          console.log('[Check Reference Stock] StateChange message ignored - processId/deviceId mismatch');
        }
      }
    };

    // Also listen to base 'state-change' event as fallback (backend might emit to base event)
    const handleStateChangeBase = (wsData: any) => {
      // Only process if siteId, zoneId, deviceId and processId match
      const wsSiteId = wsData?.data?.siteId || '';
      const wsZoneId = wsData?.data?.zoneId || '';
      if (
        wsData?.data?.processId === processId &&
        wsData?.data?.deviceId === deviceId &&
        wsData?.data?.stateName &&
        (!wsSiteId || wsSiteId === siteId) &&
        (!wsZoneId || wsZoneId === zoneId)
      ) {
        console.log('[Check Reference Stock] StateChange (base event) message received:', {
          wsData: wsData,
          stateName: wsData?.data?.stateName
        });
        
        // Update cycle counts in real-time from WebSocket event if available
        const processObject = wsData?.processObject || wsData?.data?.processObject || wsData?.data?.data?.processObject;
        const totalCycles = processObject?.totalCycles ?? wsData?.totalCycles ?? wsData?.data?.totalCycles ?? wsData?.data?.data?.totalCycles;
        const totalSuccess = processObject?.totalSuccess ?? wsData?.totalSuccess ?? wsData?.data?.totalSuccess ?? wsData?.data?.data?.totalSuccess;
        const totalFailed = processObject?.totalFailed ?? wsData?.totalFailed ?? wsData?.data?.totalFailed ?? wsData?.data?.data?.totalFailed;
        
        if (totalCycles !== undefined || totalSuccess !== undefined || totalFailed !== undefined) {
          setSyncedData((prevSyncedData: any) => {
            const keepCycles = shouldKeepPreviousTotalCycles(prevSyncedData, totalCycles);
            const updated = {
              ...prevSyncedData,
              ...(totalCycles !== undefined && !keepCycles && { totalCycles }),
              ...(totalSuccess !== undefined && { totalSuccess }),
              ...(totalFailed !== undefined && { totalFailed })
            };
            
            if (prevSyncedData?.totalCycles !== totalCycles || 
                prevSyncedData?.totalSuccess !== totalSuccess || 
                prevSyncedData?.totalFailed !== totalFailed) {
              console.log('[Check Reference Stock] Updated cycle counts from state-change (base) event:', {
                previous: {
                  totalCycles: prevSyncedData?.totalCycles,
                  totalSuccess: prevSyncedData?.totalSuccess,
                  totalFailed: prevSyncedData?.totalFailed
                },
                updated: {
                  totalCycles,
                  totalSuccess,
                  totalFailed
                },
                stateName: wsData.data.stateName,
                timestamp: new Date().toISOString()
              });
            }
            
            return updated;
          });
        }
        
        // Update timestamp of last workflow state message for cleanup logic
        lastWorkflowStateTimeRef.current = Date.now();
        
        updateNodeState(
          wsData.data.stateName,
          wsData.data.processId,
          wsData.data.deviceId
        );
      }
    };

    // Listen for Check Reference Stock specific messages
    const checkRefStockEventName = `${WebsocketKey.CHECK_REF_STOCK}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleCheckRefStockState = (wsData: any) => {
      console.log('[Check Reference Stock] CheckRefStock WebSocket message received:', {
        eventName: checkRefStockEventName,
        wsData: wsData,
        state: wsData?.state
      });
      
      const wsSiteId = wsData?.data?.siteId || '';
      const wsZoneId = wsData?.data?.zoneId || '';
      if (
        wsData?.state && 
        wsData?.data?.processId === processId && 
        wsData?.data?.deviceId === deviceId &&
        (!wsSiteId || wsSiteId === siteId) &&
        (!wsZoneId || wsZoneId === zoneId)
      ) {
        console.log('[Check Reference Stock] Updating node state from CheckRefStock message:', wsData.state);
        
        // Update cycle counts in real-time from WebSocket event if available
        const processObject = wsData?.processObject || wsData?.data?.processObject || wsData?.data?.data?.processObject;
        const totalCycles = processObject?.totalCycles ?? wsData?.totalCycles ?? wsData?.data?.totalCycles ?? wsData?.data?.data?.totalCycles;
        const totalSuccess = processObject?.totalSuccess ?? wsData?.totalSuccess ?? wsData?.data?.totalSuccess ?? wsData?.data?.data?.totalSuccess;
        const totalFailed = processObject?.totalFailed ?? wsData?.totalFailed ?? wsData?.data?.totalFailed ?? wsData?.data?.data?.totalFailed;
        
        if (totalCycles !== undefined || totalSuccess !== undefined || totalFailed !== undefined) {
          setSyncedData((prevSyncedData: any) => {
            const keepCycles = shouldKeepPreviousTotalCycles(prevSyncedData, totalCycles);
            const updated = {
              ...prevSyncedData,
              ...(totalCycles !== undefined && !keepCycles && { totalCycles }),
              ...(totalSuccess !== undefined && { totalSuccess }),
              ...(totalFailed !== undefined && { totalFailed })
            };
            
            if (prevSyncedData?.totalCycles !== totalCycles || 
                prevSyncedData?.totalSuccess !== totalSuccess || 
                prevSyncedData?.totalFailed !== totalFailed) {
              console.log('[Check Reference Stock] Updated cycle counts from check-reference-stock event:', {
                previous: {
                  totalCycles: prevSyncedData?.totalCycles,
                  totalSuccess: prevSyncedData?.totalSuccess,
                  totalFailed: prevSyncedData?.totalFailed
                },
                updated: {
                  totalCycles,
                  totalSuccess,
                  totalFailed
                },
                state: wsData.state,
                timestamp: new Date().toISOString()
              });
            }
            
            return updated;
          });
        }
        
        // Update timestamp of last workflow state message for cleanup logic
        lastWorkflowStateTimeRef.current = Date.now();
        
        updateNodeState(wsData.state);
      }
    };

    // Listen for reference stock errors from backend
    // Only show error modal when user has clicked start button
    const handleReferenceStockError = (errorData: any) => {
      console.log('[Check Reference Stock] Reference stock error received via WebSocket:', {
        errorData: errorData,
        deviceId: errorData?.deviceId,
        processId: errorData?.processId,
        message: errorData?.message,
        isStartAttempt: isStartAttemptRef.current
      });
      
      // Only show error if user has clicked the start button
      // Don't show errors when page just loads
      if (!isStartAttemptRef.current) {
        console.log('[Check Reference Stock] Ignoring reference stock error - start button not clicked');
        return;
      }
      
      // Check if error is for this device/process
      if (
        (!errorData?.deviceId || errorData?.deviceId === deviceId) &&
        (!errorData?.processId || errorData?.processId === processId)
      ) {
        // Check if error message is about reference stock not being set
        const errorMessage = errorData?.message || '';
        if (errorMessage.toLowerCase().includes('reference stock not set') || 
            errorMessage.toLowerCase().includes('set the reference stock')) {
          console.error('DeviceDetail: Reference stock error from backend (start button was clicked):', errorMessage);
          
          dispatch(setProcessRunningModal({
            visible: true,
            processName: data?.data?.processName || "Check Reference Stock",
            deviceName: data?.data?.deviceName || "Device",
            errorMessage: errorMessage || "Process has been stopped as reference stock not set for this zone. Please set the reference stock and then execute this process."
          }));
          
          // Reset starting state if process was being started
          setIsStarting(false);
          isStartAttemptRef.current = false; // Reset start attempt flag
        }
      }
    };

    // Helper function to update missing items count from WebSocket messages
    const updateMissingItemsCount = (wsData: any, source: string) => {
      let missingCount = 0;
      
      // Try multiple data structure patterns
      if (wsData?.data?.missingTags && Array.isArray(wsData.data.missingTags)) {
        missingCount = wsData.data.missingTags.length;
      } else if (wsData?.data?.missingTagsCount !== undefined) {
        missingCount = wsData.data.missingTagsCount || 0;
      } else if (wsData?.missingTags && Array.isArray(wsData.missingTags)) {
        missingCount = wsData.missingTags.length;
      } else if (wsData?.missingTagsCount !== undefined) {
        missingCount = wsData.missingTagsCount || 0;
      }
      
      if (missingCount !== missingItemsCountRef.current) {
        missingItemsCountRef.current = missingCount;
        console.log(`[Check Reference Stock] Updated missing items count from ${source}:`, {
          missingItemsCount: missingCount,
          timestamp: new Date().toISOString()
        });
      }
    };

    // Listen for edit-reference-stock-data messages to track missing items count
    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const editRefStockEventName = `${WebsocketKey.EDIT_REFERENCE_STOCK_DATA}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleEditRefStockData = (wsData: any) => {
      // Only process if this message is for our site/zone/device/process
      const wsSiteId = wsData?.data?.siteId || '';
      const wsZoneId = wsData?.data?.zoneId || '';
      if (
        (!wsData?.data?.deviceId || wsData?.data?.deviceId === deviceId) &&
        (!wsData?.data?.processId || wsData?.data?.processId === processId) &&
        (!wsSiteId || wsSiteId === siteId) &&
        (!wsZoneId || wsZoneId === zoneId)
      ) {
        updateMissingItemsCount(wsData, 'edit-reference-stock-data');
      }
    };

    // Enhanced handleCheckRefStockState to also track missing items
    const originalHandleCheckRefStockState = handleCheckRefStockState;
    const enhancedHandleCheckRefStockState = (wsData: any) => {
      // Track missing items first
      if (
        (!wsData?.data?.deviceId || wsData?.data?.deviceId === deviceId) &&
        (!wsData?.data?.processId || wsData?.data?.processId === processId)
      ) {
        updateMissingItemsCount(wsData, 'check-reference-stock');
      }
      // Then handle state change
      originalHandleCheckRefStockState(wsData);
    };

    socket.on(stateChangeEventName, handleStateChange);
    socket.on(WebsocketKey.STATE_CHANGE, handleStateChangeBase); // Fallback for base event
    socket.on(checkRefStockEventName, enhancedHandleCheckRefStockState);
    socket.on(WebsocketKey.REFERENCE_STOCK_ERROR, handleReferenceStockError);
    socket.on(editRefStockEventName, handleEditRefStockData);
    socket.on(WebsocketKey.EDIT_REFERENCE_STOCK_DATA, handleEditRefStockData); // Fallback for base event

    console.log('[Check Reference Stock] WebSocket listeners registered:', {
      stateChangeEventName,
      baseEventName: WebsocketKey.STATE_CHANGE,
      checkRefStockEventName,
      referenceStockErrorEvent: WebsocketKey.REFERENCE_STOCK_ERROR,
      editRefStockEventName,
      siteId,
      zoneId,
      deviceId,
      processId
    });

    return () => {
      socket.off(stateChangeEventName, handleStateChange);
      socket.off(WebsocketKey.STATE_CHANGE, handleStateChangeBase);
      socket.off(checkRefStockEventName, enhancedHandleCheckRefStockState);
      socket.off(WebsocketKey.REFERENCE_STOCK_ERROR, handleReferenceStockError);
      socket.off(editRefStockEventName, handleEditRefStockData);
      socket.off(WebsocketKey.EDIT_REFERENCE_STOCK_DATA, handleEditRefStockData);
      // Cleanup pending Notify transition timeout
      if (notifyTransitionTimeoutRef.current) {
        clearTimeout(notifyTransitionTimeoutRef.current);
        notifyTransitionTimeoutRef.current = null;
      }
      // Reset missing items count on cleanup
      missingItemsCountRef.current = 0;
    };
  }, [deviceId, processId, data?.data?.siteId, data?.data?.zoneId, steps, dispatch, mergedDeviceStatus, data?.data?.processName, data?.data?.deviceName]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (notifyTransitionTimeoutRef.current) {
        clearTimeout(notifyTransitionTimeoutRef.current);
        notifyTransitionTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (stopInfo?.isSuccess) {
      dispatch(removeProcessState({
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      }));
    dispatch(removeCurrentStep({
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      }));
      // Reset button to "start" when process is stopped
      dispatchStopButton("start");
      message.success({
        content: "Process Stopped Successfully",
        key: "notificationKey",
        duration: 2,
        className: "successMessage",
      });
      navigate(`../executeprocess`);
      setSyncedData({});
    }
  }, [stopInfo?.isSuccess, data?.data?.processId, data?.data?.deviceId, dispatch, navigate]);

  // Listen for paused state WebSocket messages
  useEffect(() => {
    if (!deviceId || !processId) return;

    const pausedStateEventName = `${WebsocketKey.PAUSED_STATE}-${deviceId}-${processId}`;
    const handlePausedState = (wsMessage: any) => {
      if (
        processId === wsMessage?.data?.processId &&
        deviceId === wsMessage?.data?.deviceId
    ) {
      dispatch(setIsPaused(false));
        dispatch(setPausedData(wsMessage));
      dispatch(setProcessState({
          processId: processId,
          deviceId: deviceId,
        state: "resume",
        }));
      }
    };

    socket.on(pausedStateEventName, handlePausedState);

    return () => {
      socket.off(pausedStateEventName, handlePausedState);
    };
  }, [deviceId, processId, dispatch]);

  const processNormalButton = () => {
    // Get the most up-to-date state from both Redux state and ref
    // The ref is updated immediately, while Redux state might have a slight delay
    const stateFromRedux = currentProcessState?.state;
    const stateFromRef = currentProcessStateRef.current?.state;
    const effectiveState = stateFromRef || stateFromRedux; // Prefer ref as it's more immediate
    
    // Debug: Log current state before rendering button
    console.log('[DeviceDetail] Button render - current state:', {
      stateFromRedux: stateFromRedux,
      stateFromRef: stateFromRef,
      effectiveState: effectiveState,
      isReadyForProcessing: data?.data?.isReadyForProcessing,
      shouldShowPause: isState(data?.data?.isReadyForProcessing, "start") || 
                       isState(data?.data?.isReadyForProcessing, "resume") || 
                       isState(effectiveState, "pause") || 
                       isState(effectiveState, "running"),
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId
    });
    
    return (
      <>
        <Col md={12} sm={12} lg={12}>
        {isState(effectiveState, "resume") ? 
          <UIsecondaryButton onPress={() => exitButton()}>EXIT</UIsecondaryButton> : <></>}
        </Col>
        <Col md={12} sm={12} lg={12}>
          {isState(effectiveState, "resume") ?
            <UIbutton type="info" size="sm" onPress={onResumeClick}>
              RESUME
            </UIbutton> : 
            isState(effectiveState, "pause-processing") ?
              <UIbutton type="info" size="sm" disabled>
                PAUSING...
              </UIbutton> :
            (isState(data?.data?.isReadyForProcessing, "start") || isState(data?.data?.isReadyForProcessing, "resume") || isState(effectiveState, "pause") || isState(effectiveState, "running")) ?
              <UIbutton type="info" size="sm" onPress={onPauseClick} disabled={pauseProcessInfo?.isLoading}>
                {pauseProcessInfo?.isLoading ? 'PAUSING...' : 'PAUSE'}
              </UIbutton> :
              <UIbutton 
                type="info" 
                size="sm" 
                onPress={onStartClick}
                disableBtn={isStarting || executeInfo?.isLoading || startInfo?.isLoading}
              >
                {isStarting || executeInfo?.isLoading || startInfo?.isLoading ? 'STARTING...' : 'START'}
              </UIbutton>}
        </Col>
      </>)
  }

  
  const getProcessStatus = () => {
    const currentState = currentProcessState?.state;
    const isReadyForProcessing = data?.data?.isReadyForProcessing;
    
    return (
      startInfo?.isSuccess ||
      isState(isReadyForProcessing, "pause") ||
      isState(currentState, "pause") ||
      isState(currentState, "pause-processing") ||
      isState(currentState, "resume") ||
      isState(currentState, "running") ||
      isState(isReadyForProcessing, "resume") ||
      isState(isReadyForProcessing, "start") ||
      isState(isReadyForProcessing, "pause-processing")
    );
  };

  //preview function
  const imageForProcessAction = () => {
    return steps?.map((step: any, index: any) => {
      return (
        <>
          {processName == ProcessName.CHECK_STOCK ? (
            <Col key={`step-${index}-${step?.name || index}`} span={6}>
              <div className={`stepBox ${index === stepIndex && "active"}`}>
                <div
                  className={`icon ${
                    getProcessStatus() && index === stepIndex ? "" : ""
                  }`}
                >
                  <img
                    className={
                      getProcessStatus() && index === stepIndex 
                        ? "scanTag colorIcon pulse"
                        : "scanTag"
                    }
                    src={stepProcess[step?.name]}
                    alt="uhf"
                  />
                </div>
              </div>
            </Col>
          ) : (
            <Col key={`step-${index}-${step?.name || index}`}>
              <div className={`stepBox ${index === stepIndex && "active"}`}>
                <div className={`icon ${getProcessStatus() ? "pulse" : ""}`}>
                  <img
                    className={
                      getProcessStatus() ? "scanTag colorIcon" : "scanTag"
                    }
                    src={stepProcess[step?.name]}
                    alt="uhf"
                  />
                </div>
              </div>
            </Col>
          )}
        </>
      );
    });
  };

  const exitButton = () => {
    setExitModal(true);
  };

  const onExitModalClose = () => {
    setExitModal(false);
  };

  const onPauseModalCancel = () => {
    setPauseModal(false);
  }

  const onProcessRunningModalClose = () => {
    console.log('DeviceDetail: ProcessRunningModal close requested', {
      processName: data?.data?.processName,
      deviceName: data?.data?.deviceName,
      timestamp: new Date().toISOString()
    });
    
    dispatch(setProcessRunningModal({
      visible: false,
      processName: "",
      deviceName: "",
      errorMessage: ""
    }));
  }

  var CurrentDate = moment().format("hh:mm:ss A");

  const getStepLoopAlignment = () => {
    const basis = containerWidth || window.innerWidth;
    let alignNumber = (basis * 4.263) / 100;
    let width = (basis * 8.68) / 100;
    let arrow = (basis * 4.81) / 100;

    return {
      alignNumber,
      width,
      arrow,
    };
  };

  const getStepProgressLine = () => {
    // Calculate progress line that passes through center of icons
    const totalSteps = steps?.length || 0;
    if (totalSteps === 0) return null;
    
    // Calculate icon center positions and create connecting segments
    const iconWidthPercent = 100 / totalSteps;
    const segments = [];
    
    for (let i = 0; i < totalSteps - 1; i++) {
      const isActive = i < stepIndex;
      // Start from center of current icon, end at center of next icon
      const startPercent = (i + 0.5) * iconWidthPercent;
      const endPercent = (i + 1.5) * iconWidthPercent;
      const widthPercent = endPercent - startPercent;
      
      segments.push(
        <div
          key={`segment-${i}`}
          className={`progress-line-segment ${isActive ? 'active' : ''}`}
          style={{
            left: `${startPercent}%`,
            width: `${widthPercent}%`,
          }}
        />
      );
    }
    
    return <div className="progress-line-container">{segments}</div>;
  };

  const getUpperRowStepper = () => {
    return processName == ProcessName.CHECK_STOCK ? (
      <Col span={24}>
        <Row>
          {steps?.map((step: any, index: number) => {
            return (
              <Col span={6} className="upperRow" key={`upper-step-${index}-${step?.name || index}`}>
                <span className="spanTag slice">
                  {step?.name}
                </span>
              </Col>
            );
          })}
        </Row>
      </Col>
    ) : (
      <Col className="upperRow" span={24}>
        {steps?.map((step: any, index: number) => {
          return (
            <span className="spanTag slice" key={`step-${index}-${step?.name || index}`}>
              {step?.name}
            </span>
          );
        })}
      </Col>
    );
  };

  const getClassNameForSteps = () => {
    let className = "";
    processName == ProcessName.CHECK_STOCK
      ? (className = "justify-content-center processStepsRow processCR")
      : (className = "justify-content-center processStepsRow");
    return className;
  };

  const getTimezone = (value:any) => {
    const date = new Date(value); // Use a dummy date to set time
    const options:any = { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' };

    let formattedDate = date.toLocaleString('en-US', {timeZoneName: "shortGeneric",});
    const [datePart, timePart] = formattedDate.split(', ');

    // Format the date part as MM-DD-YYYY
    const [month, day, year] = datePart.split('/');
    const formattedDateString = `${month}-${day}-${year}`;

    // Combine the formatted date with the time and timezone abbreviation
    return `${timePart}`;
  };
  const [disconnect, setDisconnect] = useState("Online")
  socket.on("disconnect",(reason:any)=>{
    console.log("<<<< disconnedteddd")
    setDisconnect("Offline")
    socket.auth = { userId: user?.userId, tenantId: tenantId }
      socket.connect()
  })
  socket.on("connect",()=>{
    setDisconnect("Online")
  })

  return (
      <>
        {/* Card 1: Details */}
        <Row className="device-detail-card" style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <div className="device-detail-card-inner">
              <h3 className="device-detail-card-heading">Details</h3>
              <div style={{ marginBottom: '12px' }}>
                <b>Site:</b> {data?.data?.siteName}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <b>Zone:</b> {data?.data?.zoneName}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <b>Device Name:</b> {data?.data?.deviceName}
              </div>
              <div style={{ marginTop: '16px', fontSize: '12px', color: '#8c8c8c' }}>
                <div>Total Scan Cycles: {syncedData?.totalCycles || 0}</div>
                <div>Unsuccessful Scans: {syncedData?.totalFailed || 0}</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Card 2: Device Status & Network Status - Optimized to only re-render when status changes */}
        <Row className="device-detail-card" style={{ marginBottom: '16px' }} key={`device-status-${deviceStatusDisplay.displayText}-${disconnect}`}>
          <Col span={24}>
            <div className="device-detail-card-inner">
              <Row>
                {/* Left Half: Device Status - Only re-renders when status changes */}
                <Col span={12} style={{ borderRight: '1px solid #e8e8e8', paddingRight: '16px' }}>
                  <div style={{ marginBottom: '4px', fontSize: '16px', fontWeight: 'bold', color: '#262626' }}>
                    Device Status
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>
                    <span style={{ color: deviceStatusDisplay.color }}>
                      {deviceStatusDisplay.displayText}
                    </span>
                  </div>
                </Col>
                {/* Right Half: Network Status */}
                <Col span={12} style={{ paddingLeft: '16px' }}>
                  <div style={{ marginBottom: '4px', fontSize: '16px', fontWeight: 'bold', color: '#262626' }}>
                    Network Status
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>
                    {disconnect === "Online" 
                          ? <span style={{ color: '#52c41a' }}>Online</span>
                          : <span style={{ color: '#ff4d4f' }}>Offline</span>}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Card 3: Process Control */}
        <Row className="device-detail-card">
          <Col span={24}>
            <div className="device-detail-card-inner">
              <h3 className="device-detail-card-heading">Process</h3>
              {/* Control Button */}
              <div style={{ marginBottom: '16px', textAlign: 'center', marginTop: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {/* Button state is driven by Redux stopbutton value, set when startProcess mutation succeeds */}
                {stopbutton === "pause" ? (
                  <div style={{ width: '50%', display: 'block' }}>
                  <UIbutton type="info" size="sm" onPress={onPauseClick} disabled={pauseProcessInfo?.isLoading}>
                    {pauseProcessInfo?.isLoading ? 'PAUSING...' : 'PAUSE'}
                  </UIbutton>
                  </div>
                ) : stopbutton === "pausing" ? (
                  <div style={{ width: '50%', display: 'block' }}>
                    <UIbutton type="info" size="sm" disabled>
                      PAUSING...
                    </UIbutton>
                  </div>
                ) : stopbutton === "resume" ? (
                  <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'center' }}>
                    <div style={{ flex: '1', maxWidth: '50%' }}>
                      <UIsecondaryButton onPress={() => exitButton()} className="device-detail-exit-button">EXIT</UIsecondaryButton>
                    </div>
                    <div style={{ flex: '1', maxWidth: '50%' }}>
                      <UIbutton type="info" size="sm" onPress={onResumeClick}>
                        RESUME
                      </UIbutton>
                    </div>
                  </div>
                ) : (
                  <div style={{ width: '50%', display: 'block' }}>
                  <UIbutton 
                    type="info" 
                    size="sm" 
                    onPress={onStartClick}
                    disableBtn={isStarting || executeInfo?.isLoading || startInfo?.isLoading}
                  >
                    {isStarting || executeInfo?.isLoading || startInfo?.isLoading ? 'STARTING...' : 'START'}
                  </UIbutton>
                  </div>
                )}
              </div>
              
              {/* Workflow Visualization */}
            <section className="stepswrapper" ref={stepsWrapperRef as any}>
                <Row gutter={[10, 10]}>
                {getUpperRowStepper()}
                <Col span={24}>
                    <Row gutter={[10, 10]} className={getClassNameForSteps()}>
                    {getStepProgressLine()}
                    {imageForProcessAction()}
                  </Row>
                </Col>
              </Row>
            </section>
            </div>
          </Col>
        </Row>
        <UIConfirmModal
          key="selectSiteDevice"
          className="siteDeviceform"
          visible={exitModal}
          cancelSiteButton={onExitModalClose}
          cancelButton={ModalButton.CANCEL}
          primaryText={ModalPrimaryText.PROCESS_EXIT}
          confirmButton={ModalButton.CONFIRM}
          type={ModalType.WARN}
          confirmCallback={onStopClick}
        />
        <UIConfirmModal
          key="pauseProcessModal"
          className="siteDeviceform"
          visible={pauseModal}
          cancelSiteButton={onPauseModalCancel}
          cancelButton={ModalButton.CANCEL}
          primaryText={ModalPrimaryText.PAUSE_PROCESS}
          confirmButton={ModalButton.CONFIRM}
          type={ModalType.WARN}
          confirmCallback={onPauseConfirmClick}
        />
      {data?.data?.deviceId === pausedData?.data?.deviceId &&
        data?.data?.processId === pausedData?.data?.processId && (
        <ProcessPausedModal
          errorDetail={pausedData?.data}
          openAlert={isState(currentProcessState?.state, "pause") || isState(currentProcessState?.state, "pause-processing")}
        />
      )}
      
      <ProcessRunningModal
        visible={processRunningModal?.visible || false}
        onClose={onProcessRunningModalClose}
        processName={processRunningModal?.processName || ""}
        deviceName={processRunningModal?.deviceName || ""}
        errorMessage={processRunningModal?.errorMessage || ""}
      />
    </>
  );
};
